/**
 * The sub-fab, built from the data in `subfabLayout.ts`.
 *
 * A different world on purpose. The cleanroom above is white, sealed and
 * silent about its own workings; the room below is bare concrete, galvanised
 * steel and colour-coded pipe, because that is what the underside of a fab
 * looks like and the contrast IS the lesson - the wafer's world upstairs is
 * manufactured by this one.
 *
 * Colour code, which is real practice (ANSI/ASME A13.1 and the way fabs
 * actually pipe) and used consistently here: the shared process-vacuum
 * header in black iron with black markers, process cooling water in green-painted steel with green
 * markers, compressed dry air in blue, nitrogen in stainless with plain
 * markers (it is neither flammable nor toxic, so the standard gives it no
 * colour of its own), ultrapure water in natural PVDF - the white plastic
 * every UPW loop is really made of - with green water markers, heating hot
 * water in red-brown, air in galvanised duct. Nitrogen and compressed air
 * are two lines, never one: an inert gas and breathing-grade air do not
 * share a pipe. A student who walks this room twice starts reading the
 * building by colour, which is exactly how a facilities technician reads it.
 *
 * WHAT THE ROOM IS MADE OF, in the order a walk reveals it: the slab with its
 * painted walkway and drain trench; the two overhead pipe racks that carry
 * every utility the length of the room on trapeze hangers, labelled with
 * flow arrows the way real pipe is; the riser bundles that branch off the
 * racks and climb into the tools standing on the floor above, each with a
 * point-of-connection placard naming its tool and the services it draws;
 * the five plant skids, each with its own local panel and live readout, its
 * gauges and its valves; the motor control centre every motor down here is
 * fed from; the exhaust scrubber the trunk duct empties into; and the
 * safety furniture - hazard bands, extinguishers, eyewash, the exit sign -
 * that says people work here.
 *
 * Everything here is data-driven from the layout module, so the harness can
 * assert the claims (every system present, every tool above has its drop,
 * opening railed, stair walkable) against the same numbers the meshes are
 * built from. Static meshes are merged per material on the way out - the
 * room is a few dozen draw calls, not a few hundred.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import {
  HOUSE_SYSTEMS,
  houseFault,
  houseSystemState,
  type HouseState,
  type HouseSystem,
} from '../sim/house/model'
import { t, tOr } from '../i18n/i18n'
import { mauStore } from '../sim/facilities/mauStore'
import {
  AIRLOCK,
  COLUMNS,
  CREW_WALK,
  DROP_FOOTPRINT,
  EXHAUST_PLANT,
  MAU,
  MCC,
  O2_MONITORS,
  PIPE_RACKS,
  RACK,
  RETURN_CHASES,
  SKIDS,
  SPRINKLERS,
  SUPPORT_GEAR,
  SUBFAB,
  TOOL_DROPS,
  UTILITY_BOARD,
  setAirlockDoor,
  type AirlockDoor,
  type Skid,
  type ToolDrop,
} from './subfabLayout'

/**
 * The colour a placard swatch or a beacon band uses for each house system -
 * the pipe or the marker band a technician would look for. `gas` is the
 * compressed-air blue; nitrogen has its own entry in `LINE` below, because
 * the two are two lines.
 */
const PIPE_COLOURS: Record<HouseSystem, number> = {
  vacuum: 0x2f3235,
  pcw: 0x2f8a55,
  gas: 0x2f62b0,
  upw: 0x2f8a55,
  hvac: 0xa8adb4,
  mau: 0xd9dde0,
  pumps: 0x5e6670,
  chem: 0x6aa0c8,
}

/**
 * Each physical line in the room: what the pipe is painted (or made of),
 * and what its marker band looks like - A13.1 puts white letters on green
 * for water and on blue for compressed air, black letters on white for
 * anything the standard leaves uncoloured, and a process-vacuum line carries
 * the same black-and-white the pipe does.
 */
const LINE = {
  vacuum: { pipe: 0x2f3235, bg: '#1e2024', fg: '#f4f6f8' },
  pcw: { pipe: 0x35704a, bg: '#1f7a3c', fg: '#f4f6f8' },
  cda: { pipe: 0x35558c, bg: '#1f4fa3', fg: '#f4f6f8' },
  n2: { pipe: 0xc9ccd0, bg: '#f2f4f6', fg: '#1e2024' },
  upw: { pipe: 0xdcdfda, bg: '#1f7a3c', fg: '#f4f6f8' },
} as const
type LineId = keyof typeof LINE

export interface Subfab {
  group: THREE.Group
  lamps: THREE.PointLight[]
  /**
   * Invisible click volumes, one per skid, each carrying its system in
   * `userData.skidSystem` - the same shape as the tool triggers upstairs.
   */
  triggers: THREE.Mesh[]
  /** Repaint the beacons, the skid readouts and the utility board from the live house state. */
  update(house: HouseState, armedId: string | null): void
  /** Per-frame motion: the fans, and the airlock doors answering the student's approach. */
  animate(dt: number, eye: THREE.Vector3): void
}

const box = (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d)
const cyl = (r: number, h: number, seg = 12) => new THREE.CylinderGeometry(r, r, h, seg)
const UP = new THREE.Vector3(0, 1, 0)

// ------------------------------------------------------------------ textures

function canvasTexture(
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
  repeat: [number, number] = [1, 1],
): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  draw(canvas.getContext('2d')!)
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(repeat[0], repeat[1])
  tex.anisotropy = 8
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/** Deterministic noise, so two builds of the room are the same room. */
function lcg(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0x100000000
  }
}

/** Sealed concrete: a mottled grey with aggregate speckle and the odd stain. One tile is 3 m. */
function concreteTexture(seed: number, base: string, dark: string): THREE.CanvasTexture {
  return canvasTexture(512, 512, (ctx) => {
    const rnd = lcg(seed)
    ctx.fillStyle = base
    ctx.fillRect(0, 0, 512, 512)
    // Broad tonal patches - the trowel marks and cure blotches of a slab.
    for (let i = 0; i < 40; i++) {
      const r = 40 + rnd() * 120
      const x = rnd() * 512
      const y = rnd() * 512
      const grad = ctx.createRadialGradient(x, y, 0, x, y, r)
      grad.addColorStop(0, rnd() > 0.5 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)')
      grad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = grad
      ctx.fillRect(x - r, y - r, r * 2, r * 2)
    }
    // Aggregate speckle.
    for (let i = 0; i < 2600; i++) {
      ctx.fillStyle = rnd() > 0.5 ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)'
      const s = 1 + rnd() * 2
      ctx.fillRect(rnd() * 512, rnd() * 512, s, s)
    }
    // A couple of old drips.
    ctx.fillStyle = dark
    for (let i = 0; i < 3; i++) {
      ctx.globalAlpha = 0.08 + rnd() * 0.08
      ctx.beginPath()
      ctx.ellipse(rnd() * 512, rnd() * 512, 20 + rnd() * 40, 12 + rnd() * 20, rnd() * 3, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1
  })
}

/**
 * The slab's WEAR, as one room-sized decal laid over everything painted.
 *
 * The concrete under it tiles every 3 m, which is right for grain and wrong
 * for use: a tiling texture repeats the same stain sixty times and says
 * nothing about where anyone walks. Wear is not a material property, it is a
 * record of what the room does - so this is drawn ONCE in room coordinates,
 * from the layout itself, and every mark in it has a cause:
 *
 * - The aisle darkens where the crew walk, hardest along the lane the
 *   technician on rounds actually paces (CREW_WALK), and pallet-jack tracks
 *   run inside it. It fades toward the two ends, where nobody goes.
 * - There is an oil shadow under every dry pump. A dry pump leaks a little
 *   from the shaft seals; that is what the drip trays under real ones are
 *   for, and it is why the floor beneath is darker than anywhere else.
 * - A scuffed patch sits in front of every skid and every piece of support
 *   gear - the square metre where a technician stands to work it, which the
 *   harness already insists must be standable.
 * - Dust banks up along the walls, where a floor scrubber never quite
 *   reaches.
 *
 * It lies ABOVE the painted lines and the joints, because dirt settles on
 * paint rather than under it, and it is transparent so the slab's own grain
 * still comes through.
 */
function floorWearTexture(): THREE.CanvasTexture {
  const W = SUBFAB.maxX - SUBFAB.minX
  const D = SUBFAB.maxZ - SUBFAB.minZ
  const cw = 1024
  const ch = Math.round((cw * D) / W)
  return canvasTexture(cw, ch, (ctx) => {
    const rnd = lcg(151)
    const px = (x: number) => ((x - SUBFAB.minX) / W) * cw
    const py = (z: number) => ((z - SUBFAB.minZ) / D) * ch
    const m = cw / W
    /** A soft dark blot in metres, centred on a point in the room. */
    const blot = (x: number, z: number, rx: number, rz: number, alpha: number, tone = '56,50,44') => {
      const cx = px(x)
      const cz = py(z)
      const r = Math.max(rx, rz) * m
      const grad = ctx.createRadialGradient(cx, cz, 0, cx, cz, r)
      grad.addColorStop(0, `rgba(${tone},${alpha})`)
      grad.addColorStop(0.55, `rgba(${tone},${alpha * 0.55})`)
      grad.addColorStop(1, `rgba(${tone},0)`)
      ctx.save()
      ctx.translate(cx, cz)
      ctx.scale(1, rz / rx)
      ctx.translate(-cx, -cz)
      ctx.fillStyle = grad
      ctx.fillRect(cx - r, cz - r, r * 2, r * 2)
      ctx.restore()
    }

    ctx.clearRect(0, 0, cw, ch)
    // Dust along the walls: a band that never gets swept.
    for (let i = 0; i < 30; i++) {
      const a = 0.17 * (1 - i / 30)
      ctx.strokeStyle = `rgba(74,70,64,${a})`
      ctx.lineWidth = 2
      ctx.strokeRect(i, i, cw - 2 * i, ch - 2 * i)
    }
    // The aisle: two lanes either side of the trench, the marked one heavier.
    for (const [z, alpha] of [
      [CREW_WALK.z, 0.3],
      [1.2, 0.19],
    ] as Array<[number, number]>) {
      for (let x = CREW_WALK.minX - 1; x < CREW_WALK.maxX + 1; x += 0.4) {
        const endFade = Math.min(1, (x - (CREW_WALK.minX - 1)) / 4, (CREW_WALK.maxX + 1 - x) / 4)
        blot(x, z + (rnd() - 0.5) * 0.5, 1.0, 0.6, alpha * endFade * (0.7 + rnd() * 0.6), '70,66,60')
      }
    }
    // Pallet-jack tracks inside the marked lane: two wheel lines, dashed
    // where the floor happens to be cleaner.
    for (const dz of [-0.38, 0.38]) {
      for (let x = CREW_WALK.minX; x < CREW_WALK.maxX; x += 0.35) {
        if (rnd() < 0.22) continue
        blot(x, CREW_WALK.z + dz, 0.22, 0.08, 0.2, '58,54,49')
      }
    }
    // An oil shadow under every dry pump, and the pump skid's own line of them.
    for (const gr of SUPPORT_GEAR.filter((g) => g.kind === 'dryPump')) {
      // Wider than the pump's own frame on purpose: the frame's base plate
      // covers the slab directly under it, so a stain drawn to the footprint
      // is a stain nobody ever sees. What shows is what has crept out from
      // under the skirt and been walked through.
      const out = gr.facing > 0 ? -1 : 1
      blot(gr.x, gr.z, 1.3, 1.05, 0.4)
      blot(gr.x + 0.25, gr.z + out * 0.75, 0.42, 0.3, 0.4)
      blot(gr.x - 0.45, gr.z + out * 0.55, 0.26, 0.2, 0.34)
    }
    for (const skid of SKIDS.filter((s) => s.kind === 'pumps')) {
      const [sx, sz] = skid.pos
      const [sw] = skid.size
      for (let x = sx - sw / 2 + 1; x < sx + sw / 2; x += 1.6) blot(x, sz + skid.facing * -1.4, 0.55, 0.4, 0.28)
    }
    // The drip under each abatement unit's tundish.
    for (const gr of SUPPORT_GEAR.filter((g) => g.kind === 'abatement')) {
      const out = gr.facing > 0 ? -1 : 1
      blot(gr.x + 0.25, gr.z + out * (gr.d / 2 - 0.06), 0.28, 0.28, 0.36, '64,60,48')
    }
    // Where people stand to work: in front of every skid and every piece of
    // gear, the patch the harness makes sure they can reach.
    const stands: Array<[number, number]> = [
      ...SKIDS.map((s) => [s.pos[0], s.pos[1] - s.facing * (s.size[1] / 2 + 0.75)] as [number, number]),
      ...SUPPORT_GEAR.map((g) => [g.x, g.z - g.facing * (g.d / 2 + 0.7)] as [number, number]),
    ]
    for (const [x, z] of stands) blot(x, z, 0.75, 0.55, 0.24, '68,64,58')
    // And a scattering of the marks nobody can account for.
    for (let i = 0; i < 40; i++) {
      blot(SUBFAB.minX + rnd() * W, SUBFAB.minZ + rnd() * D, 0.12 + rnd() * 0.5, 0.1 + rnd() * 0.35, 0.08 + rnd() * 0.14, '66,61,55')
    }
  })
}

/** Concrete masonry units in running bond: one tile is two courses of two blocks, 0.8 m by 0.4 m. */
function blockTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 256, (ctx) => {
    const rnd = lcg(7)
    ctx.fillStyle = '#8f8d8a'
    ctx.fillRect(0, 0, 512, 256)
    const draw = (x: number, y: number, w: number, h: number) => {
      const tone = 138 + Math.floor(rnd() * 14)
      ctx.fillStyle = `rgb(${tone},${tone - 2},${tone - 5})`
      ctx.fillRect(x + 4, y + 4, w - 8, h - 8)
      for (let i = 0; i < 60; i++) {
        ctx.fillStyle = rnd() > 0.5 ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)'
        ctx.fillRect(x + 6 + rnd() * (w - 12), y + 6 + rnd() * (h - 12), 2, 2)
      }
    }
    // Course 1: two full blocks. Course 2: offset by half a block.
    draw(0, 0, 256, 128)
    draw(256, 0, 256, 128)
    draw(-128, 128, 256, 128)
    draw(128, 128, 256, 128)
    draw(384, 128, 256, 128)
  })
}

/**
 * The waffle slab: one tile is 2.4 m, a 4-by-4 grid of 0.6 m coffers between
 * ribs, with one coffer in each tile an open return grille. That open cell is
 * the point of the ceiling: the cleanroom's air comes down through it, which
 * makes this room the return plenum it really is.
 */
const COFFER = {
  /** Rib centres, m. Matches the texture's 0.6 m cells, and both sit on the world grid. */
  pitch: 0.6,
  /** How far the ribs hang below the pans they frame, m - the depth of the recess. */
  depth: 0.3,
  /** Rib width, m. */
  rib: 0.12,
} as const

/**
 * The PAN of a coffer - the recessed panel seen up inside it, not the whole
 * ceiling. The ribs are geometry (see the waffle in buildSubfab), so nothing
 * here paints one.
 *
 * IT USED TO PAINT THE LOT: ribs, coffer shading and all, on a single flat
 * plane. From standing height that read as a suspended tile ceiling with air
 * diffusers in it - an office corridor, which is the one thing a subfab must
 * not look like. Painted shadow cannot survive a grazing view: the eye reads
 * depth from parallax and from the way a rib soffit catches the strip lights
 * while the pan behind it falls away, and neither of those is available to a
 * texture. So the ribs became boxes and this became what they frame.
 *
 * The vignette is the one bit of painted light left, and it earns its place:
 * the room has no shadow-casting lamps, so without a little contact darkening
 * where pan meets rib the recess reads flat however deep it is.
 */
function cofferPanTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 512, (ctx) => {
    const rnd = lcg(23)
    const cell = 128
    ctx.fillStyle = '#5f5c58'
    ctx.fillRect(0, 0, 512, 512)
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const x = i * cell
        const y = j * cell
        if (i === 1 && j === 2) {
          // The open cell: a grille over the dark of the floor void above.
          ctx.fillStyle = '#14161a'
          ctx.fillRect(x, y, cell, cell)
          ctx.fillStyle = '#4c5158'
          for (let s = y + 10; s < y + cell - 6; s += 14) ctx.fillRect(x + 8, s, cell - 16, 5)
          continue
        }
        // Formwork grain: a pan is cast against a steel or GRP dome and comes
        // out smoother than the ribs, but not clean.
        for (let k = 0; k < 90; k++) {
          ctx.fillStyle = rnd() > 0.5 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'
          ctx.fillRect(x + rnd() * cell, y + rnd() * cell, 3, 2)
        }
        // Contact darkening into the four corners of the recess.
        for (let b = 0; b < 26; b++) {
          ctx.fillStyle = `rgba(0,0,0,${(0.03 * (26 - b)) / 26})`
          ctx.fillRect(x + b, y + b, cell - 2 * b, 1)
          ctx.fillRect(x + b, y + cell - b - 1, cell - 2 * b, 1)
          ctx.fillRect(x + b, y + b, 1, cell - 2 * b)
          ctx.fillRect(x + cell - b - 1, y + b, 1, cell - 2 * b)
        }
      }
    }
  })
}

/**
 * Grime: what a painted panel looks like after a few years in a sub-fab.
 *
 * Near-white, so it MULTIPLIES a material's own colour rather than replacing
 * it - the room's colour code survives, it just stops looking like it was
 * unwrapped this morning. Three things, all low-contrast on purpose:
 *
 * - A darkening toward the panel's edges, where dust settles on a rolled lip
 *   and where hands and trolleys arrive. This is the one that does most of
 *   the work, because every box face gets its own copy of it.
 * - Soft blotches, the residue of leaks wiped off and cleaning done in a
 *   hurry.
 * - A few dribbles under the top edge.
 *
 * DELIBERATELY LOW FREQUENCY. Static geometry here is merged per material and
 * a box face carries a 0..1 UV whatever its size, so one copy of this stretches
 * across a two-metre casing and a ten-metre one alike. Anything with an edge
 * in it would show that stretch; a soft gradient cannot.
 */
function grimeTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 256, (ctx) => {
    const rnd = lcg(77)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, 256, 256)
    for (let b = 0; b < 34; b++) {
      ctx.fillStyle = `rgba(96,92,86,${(0.16 * (34 - b)) / 34})`
      ctx.fillRect(b, b, 256 - 2 * b, 1)
      ctx.fillRect(b, 255 - b, 256 - 2 * b, 1)
      ctx.fillRect(b, b, 1, 256 - 2 * b)
      ctx.fillRect(255 - b, b, 1, 256 - 2 * b)
    }
    for (let i = 0; i < 26; i++) {
      const r = 18 + rnd() * 46
      const cx = rnd() * 256
      const cy = rnd() * 256
      const blotch = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
      blotch.addColorStop(0, `rgba(104,99,92,${0.05 + rnd() * 0.05})`)
      blotch.addColorStop(1, 'rgba(104,99,92,0)')
      ctx.fillStyle = blotch
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2)
    }
    for (let i = 0; i < 7; i++) {
      const h = 20 + rnd() * 70
      const dribble = ctx.createLinearGradient(0, 0, 0, h)
      dribble.addColorStop(0, 'rgba(88,84,78,0.10)')
      dribble.addColorStop(1, 'rgba(88,84,78,0)')
      ctx.fillStyle = dribble
      ctx.fillRect(rnd() * 256, 0, 2 + rnd() * 3, h)
    }
  })
}

/** Louvre blades, tileable: what a return chase or an intake shows the room. */
function louvreTexture(): THREE.CanvasTexture {
  return canvasTexture(64, 64, (ctx) => {
    ctx.fillStyle = '#2a2e33'
    ctx.fillRect(0, 0, 64, 64)
    for (let y = 0; y < 64; y += 16) {
      const grad = ctx.createLinearGradient(0, y, 0, y + 12)
      grad.addColorStop(0, '#b9bec4')
      grad.addColorStop(1, '#6f757c')
      ctx.fillStyle = grad
      ctx.fillRect(0, y + 2, 64, 10)
    }
  })
}

/**
 * Stencilled lettering on a transparent field, for ducts and casings. A
 * trailing arrow (↑ or →) is DRAWN rather than typed: the system font's
 * arrow glyph came out as a speck on the duct.
 */
function stencilTexture(text: string, fg: string): THREE.CanvasTexture {
  const arrow = text.endsWith('↑') ? 'up' : text.endsWith('→') ? 'right' : text.endsWith('←') ? 'left' : null
  const words = arrow ? text.slice(0, -1).trim() : text
  return canvasTexture(512, 96, (ctx) => {
    ctx.clearRect(0, 0, 512, 96)
    ctx.fillStyle = fg
    ctx.font = '800 54px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const maxW = arrow ? 420 : 496
    ctx.fillText(words, arrow ? 226 : 256, 48, maxW)
    if (!arrow) return
    const w = Math.min(ctx.measureText(words).width, maxW)
    const x0 = 226 + w / 2 + 22
    ctx.beginPath()
    if (arrow === 'right') {
      ctx.moveTo(x0, 36)
      ctx.lineTo(x0 + 26, 36)
      ctx.lineTo(x0 + 26, 20)
      ctx.lineTo(x0 + 56, 48)
      ctx.lineTo(x0 + 26, 76)
      ctx.lineTo(x0 + 26, 60)
      ctx.lineTo(x0, 60)
    } else if (arrow === 'left') {
      ctx.moveTo(x0 + 56, 36)
      ctx.lineTo(x0 + 30, 36)
      ctx.lineTo(x0 + 30, 20)
      ctx.lineTo(x0, 48)
      ctx.lineTo(x0 + 30, 76)
      ctx.lineTo(x0 + 30, 60)
      ctx.lineTo(x0 + 56, 60)
    } else {
      const cx = x0 + 22
      ctx.moveTo(cx - 10, 78)
      ctx.lineTo(cx + 10, 78)
      ctx.lineTo(cx + 10, 44)
      ctx.lineTo(cx + 26, 44)
      ctx.lineTo(cx, 14)
      ctx.lineTo(cx - 26, 44)
      ctx.lineTo(cx - 10, 44)
    }
    ctx.closePath()
    ctx.fill()
  })
}

/** Yellow and black hazard stripes, diagonal, tileable. */
function hazardTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 128, (ctx) => {
    ctx.fillStyle = '#e8b83a'
    ctx.fillRect(0, 0, 128, 128)
    ctx.fillStyle = '#1e2024'
    for (let i = -2; i < 4; i++) {
      ctx.beginPath()
      ctx.moveTo(i * 64, 0)
      ctx.lineTo(i * 64 + 32, 0)
      ctx.lineTo(i * 64 + 32 + 128, 128)
      ctx.lineTo(i * 64 + 128, 128)
      ctx.closePath()
      ctx.fill()
    }
  })
}

/** Floor grating over the drain trench: dark slots on steel. */
function gratingTexture(): THREE.CanvasTexture {
  return canvasTexture(64, 64, (ctx) => {
    ctx.fillStyle = '#6a6e73'
    ctx.fillRect(0, 0, 64, 64)
    ctx.fillStyle = '#1a1c1f'
    for (let y = 4; y < 64; y += 12) ctx.fillRect(6, y, 52, 7)
  })
}

/** A pressure-gauge face, on a transparent field so the plane reads round. */
function dialTexture(): THREE.CanvasTexture {
  return canvasTexture(128, 128, (ctx) => {
    ctx.clearRect(0, 0, 128, 128)
    ctx.fillStyle = '#2b2f34'
    ctx.beginPath()
    ctx.arc(64, 64, 62, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#f2f3f0'
    ctx.beginPath()
    ctx.arc(64, 64, 54, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = '#22262b'
    ctx.lineWidth = 3
    for (let i = 0; i <= 10; i++) {
      const a = Math.PI * 0.75 + (i / 10) * Math.PI * 1.5
      ctx.beginPath()
      ctx.moveTo(64 + Math.cos(a) * 44, 64 + Math.sin(a) * 44)
      ctx.lineTo(64 + Math.cos(a) * (i % 5 === 0 ? 34 : 39), 64 + Math.sin(a) * (i % 5 === 0 ? 34 : 39))
      ctx.stroke()
    }
    ctx.strokeStyle = '#c8362a'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(64, 64)
    const a = Math.PI * 0.75 + 0.42 * Math.PI * 1.5
    ctx.lineTo(64 + Math.cos(a) * 42, 64 + Math.sin(a) * 42)
    ctx.stroke()
    ctx.fillStyle = '#22262b'
    ctx.beginPath()
    ctx.arc(64, 64, 5, 0, Math.PI * 2)
    ctx.fill()
  })
}

/**
 * A pipe marker: the service name and a flow arrow, running ALONG the pipe
 * the way real markers do. A cylinder's u runs round it and v along it, so
 * the texture is turned a quarter in UV space: the canvas's long axis lands
 * along the pipe, and the three copies stacked down the canvas land round
 * it, so the text reads from any side.
 */
function pipeLabelTexture(text: string, bg: string, fg: string): THREE.CanvasTexture {
  const tex = canvasTexture(512, 192, (ctx) => {
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, 512, 192)
    ctx.fillStyle = fg
    ctx.font = '700 34px system-ui, sans-serif'
    ctx.textBaseline = 'middle'
    for (const y of [32, 96, 160]) ctx.fillText(text, 24, y)
  })
  // Minus a quarter: plus a quarter lands the text upside down, and turning
  // flipY off instead mirrors it (checked in the room, both ways).
  tex.center.set(0.5, 0.5)
  tex.rotation = -Math.PI / 2
  return tex
}

/** A wall or column sign: a few lines of text on a coloured field. */
function signTexture(lines: string[], bg: string, fg: string, w = 256, h = 160): THREE.CanvasTexture {
  return canvasTexture(w, h, (ctx) => {
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, w, h)
    ctx.strokeStyle = fg
    ctx.lineWidth = 4
    ctx.strokeRect(6, 6, w - 12, h - 12)
    ctx.fillStyle = fg
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const size = Math.min(34, (h - 24) / lines.length - 6)
    lines.forEach((line, i) => {
      ctx.font = `${i === 0 ? 800 : 600} ${size}px system-ui, sans-serif`
      ctx.fillText(line, w / 2, 20 + ((i + 0.5) * (h - 40)) / lines.length, w - 24)
    })
  })
}

/** The point-of-connection placard on a riser bundle: the tool above, and what it draws. */
function pocTexture(drop: ToolDrop): THREE.CanvasTexture {
  return canvasTexture(256, 224, (ctx) => {
    ctx.fillStyle = '#e4e7ea'
    ctx.fillRect(0, 0, 256, 224)
    ctx.fillStyle = '#22262c'
    ctx.fillRect(0, 0, 256, 54)
    ctx.fillStyle = '#f2f4f6'
    ctx.font = '800 32px system-ui, sans-serif'
    ctx.textBaseline = 'middle'
    ctx.fillText(drop.assetTag, 14, 27)
    ctx.fillStyle = '#4a5058'
    ctx.font = '600 15px system-ui, sans-serif'
    ctx.fillText('POINT OF CONNECTION', 14, 74)
    // One row per LINE, so the dry gas lists as the two pipes it is.
    const rows: Array<[string, string]> = []
    const swatch = (id: LineId) => '#' + LINE[id].pipe.toString(16).padStart(6, '0')
    for (const s of drop.services) {
      if (s === 'vacuum') rows.push(['PROCESS VACUUM', swatch('vacuum')])
      else if (s === 'pcw') rows.push(['PCW SUPPLY / RETURN', swatch('pcw')])
      else if (s === 'upw') rows.push(['UPW SUPPLY / RETURN', swatch('upw')])
      else if (s === 'gas') rows.push(['CDA', swatch('cda')], ['NITROGEN', swatch('n2')])
      else if (s === 'hvac') rows.push(['PROCESS EXHAUST', '#a8adb4'])
    }
    if (SUPPORT_GEAR.some((gr) => gr.kind === 'dryPump' && gr.assetTag === drop.assetTag)) rows.push(['CHAMBER FORELINE', '#b9bec4'])
    rows.forEach(([name, colour], i) => {
      const y = 98 + i * 22
      ctx.fillStyle = colour
      ctx.fillRect(14, y - 7, 14, 14)
      ctx.strokeStyle = '#22262c'
      ctx.lineWidth = 1
      ctx.strokeRect(14.5, y - 6.5, 13, 13)
      ctx.fillStyle = '#22262c'
      ctx.font = '600 16px system-ui, sans-serif'
      ctx.fillText(name, 36, y)
    })
  })
}

/** A skid's name plate: asset colour band plus the system's own label. */
function placardTexture(system: HouseSystem): THREE.CanvasTexture {
  return canvasTexture(256, 96, (ctx) => {
    ctx.fillStyle = '#dfe2e6'
    ctx.fillRect(0, 0, 256, 96)
    ctx.fillStyle = '#' + PIPE_COLOURS[system].toString(16).padStart(6, '0')
    ctx.fillRect(0, 0, 256, 18)
    ctx.fillStyle = '#22262c'
    ctx.font = '600 21px system-ui, sans-serif'
    ctx.fillText(t(`house.system.${system}.Label`), 12, 58, 232)
  })
}

// ------------------------------------------------------------------- build

/**
 * Where the sub-fab's strip fixtures hang: a grid down each side of the room
 * and one over the stair. One rule for both the lamps and their battens,
 * because the two are built at different times now - see buildSubfabLamps.
 */
export function subfabLampPositions(): Array<[number, number]> {
  const s = SUBFAB.stair
  const out: Array<[number, number]> = []
  for (let x = SUBFAB.minX + 4; x < SUBFAB.maxX - 1; x += 7.5) {
    out.push([x, -5])
    out.push([x, 5])
  }
  out.push([(s.minX + s.maxX) / 2, (s.minZ + s.maxZ) / 2 - 1.6])
  return out
}

/**
 * The sub-fab's lights, without the sub-fab.
 *
 * Built at construction even when the room itself is deferred behind the
 * first frame (FabScene.ensureSubfab), because these join the fab's shared
 * lamp pool, and the per-tier visible-light budget is scanned off that pool
 * once and baked into every shader as a constant. Lamps arriving late would
 * push the count past the budget and recompile the lot mid-walk - the exact
 * hitch the budget exists to prevent. A PointLight costs nothing to make;
 * the geometry is what took the time.
 */
export function buildSubfabLamps(): THREE.PointLight[] {
  const ceilY = SUBFAB.ceilingY
  return subfabLampPositions().map(([x, z]) => {
    const lamp = new THREE.PointLight(0xf2f5ee, 7, 14, 2)
    lamp.position.set(x, ceilY - 0.4, z)
    lamp.userData.litho = false
    // Brighter per-lamp than the aisle fill: no FFU ceiling and no bright
    // walls down here, so the strips are ALL the light there is.
    lamp.userData.dim = 0.9
    lamp.userData.cullRadius = 13
    lamp.userData.subfab = true
    return lamp
  })
}

export function buildSubfab(lamps: THREE.PointLight[] = buildSubfabLamps()): Subfab {
  const g = new THREE.Group()
  const W = SUBFAB.maxX - SUBFAB.minX
  const D = SUBFAB.maxZ - SUBFAB.minZ
  const cx = (SUBFAB.minX + SUBFAB.maxX) / 2
  const cz = (SUBFAB.minZ + SUBFAB.maxZ) / 2
  const floorY = -SUBFAB.depth
  const ceilY = SUBFAB.ceilingY

  // Static meshes are collected here and merged per material at the end.
  // Anything that moves, repaints or is picked goes straight into the group.
  const statics: THREE.Mesh[] = []
  const add = (geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
    const m = new THREE.Mesh(geometry, material)
    m.position.set(x, y, z)
    statics.push(m)
    return m
  }

  // ------------------------------------------------------------- materials
  const std = (color: number, roughness: number, metalness = 0) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness })
  const concreteTex = concreteTexture(3, '#8e8b87', '#3a3634')
  concreteTex.repeat.set(W / 3, D / 3)
  const slabMat = new THREE.MeshStandardMaterial({ map: concreteTex, roughness: 0.92 })
  // The pan texture is world-aligned: ShapeGeometry hands the plane its own
  // x/z as UVs, so a 1/2.4 repeat puts a cell boundary every 0.6 m from the
  // world origin - the same grid the ribs are laid on below, which is what
  // keeps a painted grille inside a coffer instead of half-under a rib.
  const ceilTex = cofferPanTexture()
  ceilTex.repeat.set(1 / 2.4, 1 / 2.4)
  const ceilMat = new THREE.MeshStandardMaterial({ map: ceilTex, roughness: 0.95 })
  /** The rib soffits: the ceiling a student actually sees, and dirtier than the block walls. */
  const waffleMat = std(0x8d8983, 0.96)
  const louvreTex = louvreTexture()
  const louvre = new THREE.MeshStandardMaterial({ map: louvreTex, roughness: 0.5, metalness: 0.4 })
  // Painted sheet steel, dirty: one grime map shared by every panel in the
  // room. It multiplies, so the colour code is untouched - see grimeTexture.
  // The finishes here are matte because plant enamel IS matte: the room used
  // to be lit like a showroom because every casing was half a mirror.
  const grimeTex = grimeTexture()
  const painted = (color: number, roughness = 0.72, metalness = 0.08) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness, map: grimeTex })
  const casing = painted(0xd2d6d9)
  const chase = painted(0xbfc4c8, 0.68, 0.2)
  const hotWater = std(0x8a2f2a, 0.5, 0.3)
  const stencilMat = (text: string, fg = '#22262c') =>
    new THREE.MeshBasicMaterial({ map: stencilTexture(text, fg), transparent: true, alphaTest: 0.4 })
  const concrete = std(0x9b9691, 0.95)
  const concreteDark = std(0x7e7a76, 0.97)
  const blockTex = blockTexture()
  const wallMat = (lengthM: number): THREE.MeshStandardMaterial => {
    const tex = blockTex.clone()
    tex.needsUpdate = true
    tex.repeat.set(lengthM / 0.8, (SUBFAB.depth - 0.35) / 0.4)
    return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95 })
  }
  const steel = std(0xb0b5ba, 0.55, 0.5)
  const galv = std(0xa4aab0, 0.62, 0.35)
  const strut = std(0x878c92, 0.62, 0.42)
  const rod = std(0x6f7378, 0.5, 0.5)
  const rail = std(0xe8b83a, 0.55)
  const black = std(0x23262a, 0.7)
  const panelGrey = painted(0xc3c7cb)
  const panelDark = std(0x3a3f46, 0.6, 0.2)
  const jointMat = std(0x5c5955, 0.9)
  const walkway = std(0xe2b437, 0.7)
  const hazard = new THREE.MeshStandardMaterial({ map: hazardTexture(), roughness: 0.7 })
  const gratingTex = gratingTexture()
  const grating = new THREE.MeshStandardMaterial({ map: gratingTex, roughness: 0.6, metalness: 0.4 })
  // One material per physical line. Painted steel is a little rough; the
  // stainless nitrogen line and the PVDF water line are smooth and, for the
  // plastic, not metal at all.
  // PAINTED PIPE IS NOT GLOSSY. Enamel over primer on carbon steel, brushed
  // or rolled on site and then left alone in a warm room, comes out flat and
  // slightly chalky - so the two painted lines are rough and barely metallic,
  // and their colours are pulled back from the saturated green and blue they
  // were, which read as plastic under the room's lamps. The stainless
  // nitrogen line keeps its shine, because that one is genuinely polished,
  // and the PVDF water line is not metal at all.
  const lineMat: Record<LineId, THREE.MeshStandardMaterial> = {
    vacuum: std(LINE.vacuum.pipe, 0.7, 0.2),
    pcw: std(LINE.pcw.pipe, 0.74, 0.1),
    cda: std(LINE.cda.pipe, 0.74, 0.1),
    n2: std(LINE.n2.pipe, 0.32, 0.68),
    upw: std(LINE.upw.pipe, 0.42, 0.0),
  }
  const pipeMat: Record<HouseSystem, THREE.MeshStandardMaterial> = {
    vacuum: lineMat.vacuum,
    pcw: lineMat.pcw,
    gas: lineMat.cda,
    upw: lineMat.upw,
    hvac: galv,
    mau: galv,
    pumps: steel,
    chem: std(0x6aa0c8, 0.5, 0.2),
  }
  const cable = std(0x1f2226, 0.8)
  const dial = new THREE.MeshBasicMaterial({ map: dialTexture(), transparent: true, alphaTest: 0.5 })
  const pilotGreen = new THREE.MeshStandardMaterial({ color: 0x1f8f4a, emissive: 0x2fbf62, emissiveIntensity: 1.2 })
  // The amber lamp on a panel is a warning lamp: dark on a healthy plant.
  // Lit, it would say something is wrong on every skid in the room.
  const pilotAmber = new THREE.MeshStandardMaterial({ color: 0x5a4a1a, emissive: 0x8a6a20, emissiveIntensity: 0.12 })
  const pilotRed = new THREE.MeshStandardMaterial({ color: 0x4a1512, emissive: 0x7a2a22, emissiveIntensity: 0.6 })
  /** Galvanised conduit: the MCC's runs, and the drops to every machine. */
  const conduitMat = std(0x9aa0a8, 0.58, 0.35)
  const red = std(0xc8362a, 0.5, 0.2)
  const white = painted(0xe2e5e1, 0.68)
  const frp = painted(0xcfd4cb, 0.66, 0.02)
  const pumpBody = std(0x5e6670, 0.6, 0.4)
  const frameMatDark = std(0x3a3f46, 0.6, 0.3)
  // Water, so a green A13.1 marker even on a black pipe. Read from the -z
  // side, so the arrow glyphs are the mirror of the world direction: supply
  // runs east to the tower, return comes back west.
  const condenserLabels = [
    new THREE.MeshStandardMaterial({ map: pipeLabelTexture('←  COND WATER SUPPLY  ←', '#1f7a3c', '#f4f6f8'), roughness: 0.6 }),
    new THREE.MeshStandardMaterial({ map: pipeLabelTexture('→  COND WATER RETURN  →', '#1f7a3c', '#f4f6f8'), roughness: 0.6 }),
  ]
  const ahuSkin = std(0xaeb4ba, 0.5, 0.45)
  const ahuDoor = std(0x9aa0a6, 0.5, 0.45)
  const mccDoor = std(0xbcc0c5, 0.45, 0.25)
  const sightGlass = std(0x9fd0ff, 0.3, 0.2)
  const exitMat = new THREE.MeshBasicMaterial({
    map: signTexture(['EXIT'], '#1f7a3c', '#f2fff5', 256, 128),
  })

  // ---------------------------------------------------------------- the room
  const slab = add(new THREE.PlaneGeometry(W, D), slabMat, cx, floorY, cz)
  slab.rotation.x = -Math.PI / 2

  // Saw-cut control joints every 5 m, both ways - the slab's own grid.
  for (let x = SUBFAB.minX + 5; x < SUBFAB.maxX; x += 5) {
    const j = add(new THREE.PlaneGeometry(0.03, D), jointMat, x, floorY + 0.002, cz)
    j.rotation.x = -Math.PI / 2
  }
  for (let z = SUBFAB.minZ + 5; z < SUBFAB.maxZ; z += 5) {
    const j = add(new THREE.PlaneGeometry(W, 0.03), jointMat, cx, floorY + 0.002, z)
    j.rotation.x = -Math.PI / 2
  }

  // The painted pedestrian walkway down the middle, and the drain trench
  // under its centreline with grating over it. Walkway lines are what keep
  // people out from under the racks' drops.
  for (const z of [-2.0, 2.0]) {
    const line = add(new THREE.PlaneGeometry(W - 2, 0.1), walkway, cx, floorY + 0.003, z)
    line.rotation.x = -Math.PI / 2
  }
  gratingTex.repeat.set((W - 2) / 0.36, 1)
  const trench = add(new THREE.PlaneGeometry(W - 2, 0.36), grating, cx, floorY + 0.004, 0)
  trench.rotation.x = -Math.PI / 2
  // The sump the trench runs to, at the low end.
  add(box(0.9, 0.02, 0.9), grating, SUBFAB.maxX - 1.6, floorY + 0.006, 0)

  // The wear, over the lot: one decal in room coordinates, above the paint
  // because dirt settles on paint. Not merged with anything - it is the only
  // transparent thing lying on the slab, and it must draw last.
  {
    const wearTex = floorWearTexture()
    const wear = new THREE.Mesh(
      new THREE.PlaneGeometry(W, D),
      new THREE.MeshStandardMaterial({
        map: wearTex,
        transparent: true,
        depthWrite: false,
        roughness: 0.97,
        polygonOffset: true,
        polygonOffsetFactor: -2,
      }),
    )
    wear.rotation.x = -Math.PI / 2
    wear.position.set(cx, floorY + 0.007, cz)
    wear.renderOrder = 1
    g.add(wear)
  }

  // Underside of the fab floor, with the stairwell cut out of it. The +x/2
  // rotation below maps shape y straight onto world z (the floor above uses
  // the OPPOSITE rotation and so maps y to MINUS z - easy to conflate, and
  // conflating it put this ceiling a full room-width off).
  const shape = new THREE.Shape()
  shape.moveTo(SUBFAB.minX, SUBFAB.minZ)
  shape.lineTo(SUBFAB.maxX, SUBFAB.minZ)
  shape.lineTo(SUBFAB.maxX, SUBFAB.maxZ)
  shape.lineTo(SUBFAB.minX, SUBFAB.maxZ)
  const s = SUBFAB.stair
  const hole = new THREE.Path()
  hole.moveTo(s.minX, s.minZ)
  hole.lineTo(s.maxX, s.minZ)
  hole.lineTo(s.maxX, s.maxZ)
  hole.lineTo(s.minX, s.maxZ)
  shape.holes.push(hole)
  // The pans sit a coffer's depth UP INSIDE the slab, not below it: the rib
  // soffits are the ceiling plane at ceilY, so the recess is cut into the
  // structure and the room keeps every millimetre of its 3.25 m. Hanging the
  // ribs downward instead would have cost 300 mm of headroom and left them
  // fouling the exhaust trunk, whose top clears the downstand beams by 20 mm.
  const ceiling = new THREE.Mesh(new THREE.ShapeGeometry(shape), ceilMat)
  ceiling.rotation.x = Math.PI / 2
  ceiling.position.y = ceilY + COFFER.depth
  g.add(ceiling)

  // The ribs themselves, on the world 0.6 m grid, with one laid along each
  // wall line so the perimeter coffer is closed off rather than open to the
  // slot above the blockwork.
  const ribLines = (from: number, to: number): number[] => {
    const out = [from]
    const first = Math.ceil(from / COFFER.pitch + 1e-6)
    for (let k = first; k * COFFER.pitch < to - 1e-6; k++) out.push(k * COFFER.pitch)
    out.push(to)
    return out
  }
  /**
   * One rib, split around the stairwell so the ceiling keeps its hole - the
   * opening a student walks down through has to stay an opening.
   */
  const rib = (along: 'x' | 'z', at: number, from: number, to: number): void => {
    const across = along === 'x' ? [s.minZ, s.maxZ] : [s.minX, s.maxX]
    const cut = along === 'x' ? [s.minX, s.maxX] : [s.minZ, s.maxZ]
    const spans =
      at > across[0] && at < across[1]
        ? [
            [from, cut[0]],
            [cut[1], to],
          ]
        : [[from, to]]
    for (const [a, b] of spans) {
      if (b - a < 0.05) continue
      const geo =
        along === 'x'
          ? box(b - a, COFFER.depth, COFFER.rib)
          : box(COFFER.rib, COFFER.depth, b - a)
      const mid = (a + b) / 2
      const y = ceilY + COFFER.depth / 2
      add(geo, waffleMat, along === 'x' ? mid : at, y, along === 'x' ? at : mid)
    }
  }
  for (const x of ribLines(SUBFAB.minX, SUBFAB.maxX)) rib('z', x, SUBFAB.minZ, SUBFAB.maxZ)
  for (const z of ribLines(SUBFAB.minZ, SUBFAB.maxZ)) rib('x', z, SUBFAB.minX, SUBFAB.maxX)

  // Downstand beams both ways, on the column lines - a beam spans column
  // to column, and the racks hang under the longitudinal ones because that
  // is where the structure is.
  for (const [x] of COLUMNS.filter(([, z]) => z < 0)) {
    add(box(0.3, 0.35, D), concrete, x, ceilY - 0.17, cz)
  }
  for (const z of PIPE_RACKS.map((r) => r.z)) {
    add(box(W, 0.35, 0.3), concrete, cx, ceilY - 0.17, z)
  }

  // Perimeter walls, slab to ceiling, in block.
  const wallH = SUBFAB.depth - 0.35
  const wallY = floorY + wallH / 2
  add(new THREE.PlaneGeometry(W, wallH), wallMat(W), cx, wallY, SUBFAB.minZ + 0.13)
  const south = add(new THREE.PlaneGeometry(W, wallH), wallMat(W), cx, wallY, SUBFAB.maxZ - 0.13)
  south.rotation.y = Math.PI
  const west = add(new THREE.PlaneGeometry(D, wallH), wallMat(D), SUBFAB.minX + 0.13, wallY, cz)
  west.rotation.y = Math.PI / 2
  const east = add(new THREE.PlaneGeometry(D, wallH), wallMat(D), SUBFAB.maxX - 0.13, wallY, cz)
  east.rotation.y = -Math.PI / 2
  // A painted dado line at waist height round the room, and the solid wall
  // behind the block skin so nothing is see-through at a corner.
  for (const [x, z, w, d] of [
    [cx, SUBFAB.minZ, W, 0.25],
    [cx, SUBFAB.maxZ, W, 0.25],
    [SUBFAB.minX, cz, 0.25, D],
    [SUBFAB.maxX, cz, 0.25, D],
  ] as Array<[number, number, number, number]>) {
    add(box(w, wallH, d), concrete, x, wallY, z)
  }

  for (const [x, z] of COLUMNS) {
    add(box(0.6, wallH, 0.6), concrete, x, wallY, z)
    // Hazard band at the base: the thing a forklift would hit.
    add(box(0.64, 0.45, 0.64), hazard, x, floorY + 0.225, z)
  }

  // ---------------------------------------------------------------- the stair
  const run = s.maxX - s.minX
  const stairW = s.maxZ - s.minZ
  const steps = Math.round(SUBFAB.depth / 0.18)
  for (let i = 0; i < steps; i++) {
    const x0 = s.minX + (i * run) / steps
    const y = (-(i + 1) * SUBFAB.depth) / steps
    add(box(run / steps + 0.02, 0.08, stairW - 0.1), steel, x0 + run / steps / 2, y + 0.04, (s.minZ + s.maxZ) / 2)
    // The riser face, so the stair reads solid from the front; the nosing in
    // yellow, which is what a stair is required to carry.
    add(box(0.03, SUBFAB.depth / steps, stairW - 0.1), concreteDark, x0, y + SUBFAB.depth / steps / 2, (s.minZ + s.maxZ) / 2)
    add(box(0.05, 0.02, stairW - 0.1), walkway, x0 + 0.02, y + 0.09, (s.minZ + s.maxZ) / 2)
  }

  // Cheek walls: solid from the slab to the fab floor, so the stair reads
  // enclosed from below. They stop at floor level because the airlock shaft
  // walls below take over from there, on the same plane and at the same
  // thickness - carrying the concrete on up through them z-fought the shaft
  // panel along the whole length of the stair.
  for (const z of [s.minZ, s.maxZ]) {
    add(box(run + 0.4, SUBFAB.depth, 0.12), concrete, s.minX + run / 2 + 0.1, -SUBFAB.depth / 2, z)
    add(box(run + 0.4, 0.06, 0.18), rail, s.minX + run / 2 + 0.1, 1.12, z)
    // A handrail down the stair on each cheek, at hand height above the treads.
    const from = new THREE.Vector3(s.minX, 0.95, z + (z === s.minZ ? 0.1 : -0.1))
    const to = new THREE.Vector3(s.maxX, -SUBFAB.depth + 0.95, z + (z === s.minZ ? 0.1 : -0.1))
    pipe(rail, from, to, 0.025)
  }
  // Far-end guardrail at fab level - the stair passes underneath it.
  add(box(0.12, 1.15, stairW), rail, s.maxX + 0.22, 0.55, (s.minZ + s.maxZ) / 2)

  // ---------------------------------------------------------------- the airlock
  //
  // The stair is a shaft, not a hole: walls and a roof enclose it at fab
  // level from the vestibule to its far end, a door seals each end, and the
  // two doors are interlocked so the cleanroom's air never meets this
  // room's. Each door is one leaf that slides into a pocket; the interlock
  // lamp beside it says whether it may open.
  const zc = (s.minZ + s.maxZ) / 2
  const panelWhite = std(0xe9ecef, 0.6)
  const shaftMinX = AIRLOCK.vestibule.minX - 0.1
  const shaftMaxX = s.maxX + 0.5
  const shaftLen = shaftMaxX - shaftMinX
  const shaftCx = (shaftMinX + shaftMaxX) / 2
  const H_SHAFT = AIRLOCK.vestibule.height
  // The far-side wall in one run; the aisle-side wall in two, either side
  // of the upper door's opening; and the vestibule's west end.
  add(box(shaftLen, H_SHAFT, 0.12), panelWhite, shaftCx, H_SHAFT / 2, s.maxZ)
  {
    const up = AIRLOCK.upperDoor
    const x0 = up.x - up.width / 2
    const x1 = up.x + up.width / 2
    add(box(x0 - shaftMinX, H_SHAFT, 0.12), panelWhite, (shaftMinX + x0) / 2, H_SHAFT / 2, s.minZ)
    add(box(shaftMaxX - x1, H_SHAFT, 0.12), panelWhite, (x1 + shaftMaxX) / 2, H_SHAFT / 2, s.minZ)
    add(box(up.width, H_SHAFT - 2.3, 0.12), panelWhite, up.x, 2.3 + (H_SHAFT - 2.3) / 2, s.minZ)
    add(box(0.2, H_SHAFT, stairW + 0.24), panelWhite, shaftMinX + 0.1, H_SHAFT / 2, zc)
  }
  add(box(shaftLen, 0.1, stairW + 0.24), panelWhite, shaftCx, H_SHAFT + 0.05, zc)
  add(box(0.2, H_SHAFT, stairW + 0.24), panelWhite, shaftMaxX - 0.1, H_SHAFT / 2, zc)
  // The tacky mat inside the vestibule: the last clean surface.
  const mat = add(new THREE.PlaneGeometry(2.0, stairW - 0.2), std(0x2a3f6b, 0.6), AIRLOCK.vestibule.minX + 1.2, 0.004, zc)
  mat.rotation.x = -Math.PI / 2
  // Wayfinding on the wall facing the upper door. Coming in from the chase
  // you arrive facing this wall from 1.6 m away with the stair out of view
  // on your left - which read as an empty room until this sign was here.
  // The plane is turned to face -z, so its left is world +x: the stair.
  const wayfinding = new THREE.Mesh(
    new THREE.PlaneGeometry(1.0, 0.5),
    new THREE.MeshBasicMaterial({ map: signTexture(['←  STAIR DOWN', 'SUB-FAB PLANT LEVEL'], '#f2f4f6', '#22262c', 320, 160) }),
  )
  wayfinding.position.set(AIRLOCK.upperDoor.x, 1.6, s.maxZ - 0.07)
  wayfinding.rotation.y = Math.PI
  g.add(wayfinding)

  interface Door {
    leaf: THREE.Mesh
    /** Which way the leaf slides, where it sits shut, and how far it travels into its pocket. */
    axis: 'x' | 'z'
    closed: number
    travel: number
    centre: THREE.Vector3
    /** Who may open it: a person at fab level, or one down here. */
    level: 'fab' | 'slab'
    free: THREE.MeshStandardMaterial
    locked: THREE.MeshStandardMaterial
    open: number
  }
  const doorLeafMat = std(0xd5d9dd, 0.5, 0.2)
  /** The interlock lamps and the sign a door carries, on the side a person approaches from. */
  const doorFurniture = (signLines: string[], x: number, y: number, z: number, out: 'x+' | 'x-' | 'z-'): Pick<Door, 'free' | 'locked'> => {
    const free = new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x2fbf62, emissiveIntensity: 1.0 })
    const locked = new THREE.MeshStandardMaterial({ color: 0x4a1512, emissive: 0xd23a2e, emissiveIntensity: 0.2 })
    const nx = out === 'x+' ? 1 : out === 'x-' ? -1 : 0
    const nz = out === 'z-' ? -1 : 0
    add(box(nz ? 0.14 : 0.05, 0.22, nz ? 0.05 : 0.14), panelDark, x, y, z)
    for (const [m, dy] of [
      [free, 0.05],
      [locked, -0.05],
    ] as Array<[THREE.MeshStandardMaterial, number]>) {
      const lamp = new THREE.Mesh(cyl(0.022, 0.02, 10), m)
      if (nz) lamp.rotation.x = Math.PI / 2
      else lamp.rotation.z = Math.PI / 2
      lamp.position.set(x + nx * 0.03, y + dy, z + nz * 0.03)
      g.add(lamp)
    }
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.62, 0.39), new THREE.MeshBasicMaterial({ map: signTexture(signLines, '#f2f4f6', '#22262c') }))
    sign.position.set(x + nx * 0.03 + (nz ? 0.75 : 0), y + 0.5, z + nz * 0.03 + (nx ? -0.2 : 0))
    sign.rotation.y = out === 'x-' ? -Math.PI / 2 : out === 'x+' ? Math.PI / 2 : Math.PI
    g.add(sign)
    return { free, locked }
  }
  /** A door across the stair (an x plane), sliding into a pocket on the -z side: the lower door. */
  const makeStairDoor = (x: number, baseY: number, level: 'fab' | 'slab', signLines: string[], signX: number, signOut: 'x+' | 'x-'): Door => {
    for (const z of [s.minZ - 0.03, s.maxZ + 0.03]) add(box(0.16, 2.3, 0.08), strut, x, baseY + 1.15, z)
    add(box(0.16, 0.12, stairW + 0.2), strut, x, baseY + 2.24, zc)
    add(box(0.1, 2.3, 1.62), panelWhite, x, baseY + 1.15, s.minZ - 0.81)
    const leaf = new THREE.Mesh(box(0.06, 2.18, stairW - 0.02), doorLeafMat)
    leaf.position.set(x, baseY + 1.09, zc)
    const window = new THREE.Mesh(box(0.07, 0.5, 0.5), black)
    window.position.set(0, 0.45, 0)
    leaf.add(window)
    const pull = new THREE.Mesh(box(0.03, 0.3, 0.03), strut)
    pull.position.set(0.05, -0.1, 0.55)
    leaf.add(pull)
    g.add(leaf)
    const { free, locked } = doorFurniture(signLines, signX, baseY + 1.45, s.minZ - 0.35, signOut)
    return { leaf, axis: 'z', closed: zc, travel: stairW + 0.02, centre: new THREE.Vector3(x, baseY + 1.2, zc), level, free, locked, open: 0 }
  }
  /**
   * The upper door: in the vestibule's aisle-side wall, facing the chase
   * corridor, its leaf sliding west along that wall into a pocket. The wall
   * itself is built in two runs either side of the opening.
   */
  const makeCorridorDoor = (signLines: string[]): Door => {
    const up = AIRLOCK.upperDoor
    const x0 = up.x - up.width / 2
    const x1 = up.x + up.width / 2
    for (const x of [x0 - 0.03, x1 + 0.03]) add(box(0.08, 2.3, 0.16), strut, x, 1.15, up.z)
    add(box(up.width + 0.2, 0.12, 0.16), strut, up.x, 2.24, up.z)
    // The pocket panel, west of the opening along the same wall line.
    add(box(up.width + 0.02, 2.3, 0.1), panelWhite, x0 - up.width / 2 - 0.01, 1.15, up.z)
    const leaf = new THREE.Mesh(box(up.width - 0.02, 2.18, 0.06), doorLeafMat)
    leaf.position.set(up.x, 1.09, up.z)
    const window = new THREE.Mesh(box(0.5, 0.5, 0.07), black)
    window.position.set(0, 0.45, 0)
    leaf.add(window)
    const pull = new THREE.Mesh(box(0.03, 0.3, 0.03), strut)
    pull.position.set(up.width / 2 - 0.25, -0.1, -0.05)
    leaf.add(pull)
    g.add(leaf)
    const { free, locked } = doorFurniture(signLines, x1 + 0.35, 1.45, up.z - 0.09, 'z-')
    return { leaf, axis: 'x', closed: up.x, travel: up.width + 0.02, centre: new THREE.Vector3(up.x, 1.2, up.z), level: 'fab', free, locked, open: 0 }
  }
  const doors: Record<AirlockDoor, Door> = {
    upper: makeCorridorDoor(['AIRLOCK', 'SUB-FAB BEYOND', 'NOT A CLEANROOM', 'ONE DOOR AT A TIME']),
    lower: makeStairDoor(AIRLOCK.lowerDoorX, floorY, 'slab', ['AIRLOCK', 'CLEANROOM BEYOND', 'GOWN REQUIRED', 'ONE DOOR AT A TIME'], AIRLOCK.lowerDoorX + 0.09, 'x+'),
  }
  // The exit sign over the stair foot, and the emergency light beside it.
  add(box(0.42, 0.2, 0.06), exitMat, s.maxX + 0.5, ceilY - 0.3, (s.minZ + s.maxZ) / 2)
  add(box(0.3, 0.12, 0.12), panelGrey, s.maxX + 1.2, ceilY - 0.25, s.maxZ - 0.2)
  for (const dx of [-0.09, 0.09]) add(cyl(0.05, 0.08, 10), white, s.maxX + 1.2 + dx, ceilY - 0.36, s.maxZ - 0.24)

  // ------------------------------------------------------------- piping kit
  function pipe(mat: THREE.Material, from: THREE.Vector3, to: THREE.Vector3, radius = 0.09): THREE.Mesh {
    const dir = to.clone().sub(from)
    const len = dir.length()
    const m = new THREE.Mesh(cyl(radius, len, radius > 0.12 ? 14 : 10), mat)
    m.position.copy(from).addScaledVector(dir, 0.5)
    m.quaternion.setFromUnitVectors(UP, dir.normalize())
    statics.push(m)
    return m
  }
  /** A run of pipe through several points, with a ball at each corner so elbows read as elbows. */
  function pipeRun(mat: THREE.Material, points: THREE.Vector3[], radius: number): void {
    for (let i = 0; i + 1 < points.length; i++) pipe(mat, points[i], points[i + 1], radius)
    for (let i = 1; i + 1 < points.length; i++) {
      add(new THREE.SphereGeometry(radius * 1.02, 10, 8), mat, points[i].x, points[i].y, points[i].z)
    }
  }
  /** A rectangular duct through several points. */
  function ductRun(points: THREE.Vector3[], w: number, h: number): void {
    for (let i = 0; i + 1 < points.length; i++) {
      const a = points[i]
      const b = points[i + 1]
      const dx = Math.abs(b.x - a.x)
      const dy = Math.abs(b.y - a.y)
      const dz = Math.abs(b.z - a.z)
      add(
        box(dx > 0 ? dx + w : w, dy > 0 ? dy + h : h, dz > 0 ? dz + w : w),
        galv,
        (a.x + b.x) / 2,
        (a.y + b.y) / 2,
        (a.z + b.z) / 2,
      )
    }
  }
  /** A flange pair at a joint on a horizontal pipe along x. */
  function flangeX(mat: THREE.Material, x: number, y: number, z: number, r: number): void {
    for (const dx of [-0.045, 0.045]) {
      const f = add(cyl(r + 0.05, 0.035, 14), mat, x + dx, y, z)
      f.rotation.z = Math.PI / 2
    }
  }
  /** A hand valve: body on the pipe, handwheel above it. */
  function valve(mat: THREE.Material, x: number, y: number, z: number, r: number, along: 'x' | 'y' | 'z'): void {
    add(box(r * 3.2, r * 3.2, r * 3.2), mat, x, y, z)
    const stemLen = r * 3.5
    const wheelR = r * 2.2
    if (along === 'y') {
      // A vertical pipe: the wheel stands off sideways.
      add(cyl(0.015, stemLen, 6), steel, x + stemLen / 2, y, z).rotation.z = Math.PI / 2
      const wheel = add(new THREE.TorusGeometry(wheelR, 0.014, 6, 16), red, x + stemLen, y, z)
      wheel.rotation.y = Math.PI / 2
    } else {
      add(cyl(0.015, stemLen, 6), steel, x, y + stemLen / 2, z)
      const wheel = add(new THREE.TorusGeometry(wheelR, 0.014, 6, 16), red, x, y + stemLen, z)
      wheel.rotation.x = Math.PI / 2
    }
  }
  /**
   * A fire damper where a duct passes the slab. The damper itself is inside
   * the duct at the penetration; what the room sees is the sleeve through
   * the slab, the access door a technician resets it through, and the red
   * plate that says it is there - so the rating of the floor survives the
   * hole, and someone can find the damper when it has dropped.
   */
  const fdPlate = new THREE.MeshBasicMaterial({ map: signTexture(['FIRE DAMPER'], '#b3261e', '#ffffff', 256, 72) })
  function fireDamper(x: number, z: number, w: number, d: number, face: 1 | -1 = -1): void {
    add(box(w + 0.1, 0.16, d + 0.1), galv, x, ceilY - 0.09, z)
    add(box(0.3, 0.26, 0.012), mccDoor, x, ceilY - 0.36, z + face * (d / 2 + 0.006))
    const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.084), fdPlate)
    plate.position.set(x, ceilY - 0.55, z + face * (d / 2 + 0.012))
    plate.rotation.y = face > 0 ? 0 : Math.PI
    g.add(plate)
  }
  /** Stencilled lettering on a face. `out` is the face's outward normal, along z or x. */
  function stencil(text: string, x: number, y: number, z: number, out: 'z+' | 'z-' | 'x+' | 'x-', w = 1.2, fg = '#22262c'): void {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, w * 0.1875), stencilMat(text, fg))
    m.position.set(x, y, z)
    m.rotation.y = out === 'z+' ? 0 : out === 'z-' ? Math.PI : out === 'x+' ? Math.PI / 2 : -Math.PI / 2
    g.add(m)
  }
  /** A pressure gauge on a face: the case, and the dial on its front. */
  function gauge(x: number, y: number, z: number, out: 1 | -1, r = 0.09): void {
    const c = add(cyl(r, 0.05, 16), black, x, y, z)
    c.rotation.x = Math.PI / 2
    const face = add(new THREE.PlaneGeometry(r * 1.9, r * 1.9), dial, x, y, z + out * 0.027)
    face.rotation.y = out > 0 ? 0 : Math.PI
    // The stem into whatever it reads.
    add(cyl(0.014, 0.12, 6), steel, x, y - r - 0.06, z)
  }

  // ------------------------------------------------------------- the racks
  //
  // Longitudinal services on the lower tier, spread across the strut; the
  // exhaust trunk and the cable tray above. Each rack carries its pipes the
  // length of the room, so the branch to any drop is a short hop.
  interface Service {
    system: HouseSystem
    line: LineId
    /** Offset across the rack, m. */
    dz: number
    r: number
    label: string
    role: 'supply' | 'return'
    /**
     * Which way the fluid goes along the room, as a world direction: '→' is
     * +x. Set by where each plant stands - cooling water leaves the chiller
     * at the west end, UPW leaves its plant at the east end, vacuum is
     * pulled toward the pumps, air and nitrogen fan out from mid-room.
     */
    flow: '→' | '←'
    /** Where this line's riser stands in a tool's bundle, m from the bundle's x. */
    riserDx: number
    /** Whether a tool's branch carries an isolation valve at hand height. */
    valved: boolean
  }
  // Sizes are a section's, not a site's: a 200 mm house-vacuum header, DN150
  // cooling water, DN100 UPW, DN80 air, DN65 nitrogen. Supply and return of
  // a pair ride side by side on the rack and rise side by side in a bundle.
  const SERVICES: Service[] = [
    { system: 'vacuum', line: 'vacuum', dz: -0.58, r: 0.11, label: 'PROCESS VACUUM', role: 'supply', flow: '←', riserDx: -0.5, valved: true },
    { system: 'pcw', line: 'pcw', dz: -0.3, r: 0.085, label: 'PCW SUPPLY', role: 'supply', flow: '→', riserDx: -0.22, valved: true },
    { system: 'pcw', line: 'pcw', dz: -0.1, r: 0.085, label: 'PCW RETURN', role: 'return', flow: '←', riserDx: -0.02, valved: false },
    { system: 'upw', line: 'upw', dz: 0.08, r: 0.06, label: 'UPW SUPPLY', role: 'supply', flow: '←', riserDx: 0.16, valved: true },
    { system: 'upw', line: 'upw', dz: 0.24, r: 0.06, label: 'UPW RETURN', role: 'return', flow: '→', riserDx: 0.32, valved: false },
    { system: 'gas', line: 'cda', dz: 0.4, r: 0.045, label: 'CDA', role: 'supply', flow: '←', riserDx: 0.48, valved: true },
    { system: 'gas', line: 'n2', dz: 0.54, r: 0.035, label: 'NITROGEN', role: 'supply', flow: '←', riserDx: 0.62, valved: true },
  ]
  /**
   * Marker bands, made once per rack side. A band reads from the walkway,
   * and the two racks are read from opposite sides: on the +z rack the
   * cylinder is turned the other way so the words are upright, which would
   * mirror the arrows - so the arrow glyph is swapped there too, and '→'
   * means +x in the world on both racks.
   */
  const labelMatsFor = (flip: boolean) =>
    SERVICES.map((sv) => {
      const { bg, fg } = LINE[sv.line]
      const arrow = flip ? (sv.flow === '→' ? '←' : '→') : sv.flow
      return new THREE.MeshStandardMaterial({ map: pipeLabelTexture(`${arrow}  ${sv.label}  ${arrow}`, bg, fg), roughness: 0.6 })
    })
  const labelMats = { plain: labelMatsFor(false), flipped: labelMatsFor(true) }
  const upperY = ceilY - RACK.upperDrop
  const lowerY = ceilY - RACK.lowerDrop
  /** Where a service line runs: its height and its z on a given rack. */
  const lineY = (sv: Service) => lowerY + 0.0225 + sv.r
  /** The height a branch crosses the rack at: over the lower tier, under the trunk. */
  const HOP_Y = lowerY + 0.4
  const lineOf = (line: LineId, role: 'supply' | 'return' = 'supply') => SERVICES.find((sv) => sv.line === line && sv.role === role)!

  for (const rack of PIPE_RACKS) {
    const len = rack.maxX - rack.minX
    const rx = (rack.minX + rack.maxX) / 2
    const side = Math.sign(rack.z) || 1
    const flip = side > 0
    const mats = flip ? labelMats.flipped : labelMats.plain
    // Trapeze hangers: two threaded rods from the slab to each tier's strut.
    for (let x = rack.minX + 1; x <= rack.maxX; x += RACK.hangerSpacing) {
      for (const dz of [-RACK.halfWidth, RACK.halfWidth]) {
        add(cyl(0.012, ceilY - lowerY + 0.05, 6), rod, x, (ceilY + lowerY) / 2, rack.z + dz)
      }
      add(box(0.045, 0.045, RACK.halfWidth * 2 + 0.1), strut, x, upperY, rack.z)
      add(box(0.045, 0.045, RACK.halfWidth * 2 + 0.1), strut, x, lowerY, rack.z)
    }
    // Lower tier: the pipes, resting on the strut.
    SERVICES.forEach((sv, i) => {
      const y = lineY(sv)
      const z = rack.z + sv.dz
      pipe(lineMat[sv.line], new THREE.Vector3(rack.minX, y, z), new THREE.Vector3(rack.maxX, y, z), sv.r)
      // Flanged joints every 6 m on the steel lines; PVDF is butt-welded and
      // shows no flanges. Markers every 8 m on all of them.
      if (sv.r >= 0.08 && sv.line !== 'upw') for (let x = rack.minX + 3; x < rack.maxX; x += 6) flangeX(steel, x, y, z, sv.r)
      for (let x = rack.minX + 4 + i * 0.7; x < rack.maxX - 1; x += 8) {
        add(cyl(sv.r + 0.008, 0.5, 14), mats[i], x, y, z).rotation.z = flip ? -Math.PI / 2 : Math.PI / 2
      }
    })
    // Upper tier: the exhaust trunk on the outer side, the cable tray inboard.
    const ductZ = rack.z + side * 0.4
    add(box(len, 0.36, 0.7), galv, rx, upperY + 0.24, ductZ)
    for (let x = rack.minX + 2; x < rack.maxX; x += 4) add(box(0.06, 0.42, 0.76), strut, x, upperY + 0.24, ductZ)
    // The trunk says what it is and which way it flows, on the face the walkway sees.
    // Both trunks flow east to the scrubber. The south rack's face is read
    // from the north, where a right-hand glyph points west, so it gets the
    // left-hand one: the arrow means +x on both.
    for (let x = rack.minX + 6; x < rack.maxX - 2; x += 10) {
      stencil(side > 0 ? 'PROCESS EXHAUST ←' : 'PROCESS EXHAUST →', x, upperY + 0.24, ductZ - side * 0.356, side > 0 ? 'z-' : 'z+', 1.3, '#f2f4f6')
    }
    const trayZ = rack.z - side * 0.45
    for (const dz of [-0.25, 0.25]) add(box(len, 0.08, 0.03), strut, rx, upperY + 0.065, trayZ + dz)
    for (let x = rack.minX + 0.15; x < rack.maxX; x += 0.3) add(box(0.03, 0.03, 0.5), strut, x, upperY + 0.04, trayZ)
    for (const [dz, r] of [
      [-0.14, 0.022],
      [-0.03, 0.02],
      [0.08, 0.024],
      [0.17, 0.018],
    ] as Array<[number, number]>) {
      pipe(cable, new THREE.Vector3(rack.minX, upperY + 0.06 + r, trayZ + dz), new THREE.Vector3(rack.maxX, upperY + 0.06 + r, trayZ + dz), r)
    }
  }

  // ------------------------------------------------------------- tool drops
  //
  // A riser bundle per tool above: each service leaves its rack line, hops
  // over the rest of the tier, runs out to the bundle and climbs to the
  // slab. Isolation valves at hand height on the lines that have them, and
  // the placard that names the tool.
  /** The exhaust duct riser stands at the +x end of every bundle; the tool's own foreline, when it has one, at the -x end. */
  const RISER_DX = { hvac: 0.9, foreline: -0.76 } as const
  for (const drop of TOOL_DROPS) {
    const rack = PIPE_RACKS.reduce((a, b) => (Math.abs(b.z - drop.z) < Math.abs(a.z - drop.z) ? b : a))
    const side = Math.sign(rack.z) || 1
    const hopY = HOP_Y
    const services = SERVICES.filter((sv) => drop.services.includes(sv.system))
    services.forEach((sv, i) => {
      const mainY = lineY(sv)
      const mainZ = rack.z + sv.dz
      const rxDrop = drop.x + sv.riserDx
      // A point of connection is a drop AND a rise: the line comes down off
      // the rack to a valve station at hand height on the walkway side of
      // the bundle, crosses at knee height, and climbs through the slab on
      // the tool side. That is what puts the isolation valve where a hand
      // can reach it, which is the whole reason the station exists.
      const zDown = drop.z - side * 0.22
      const zUp = drop.z + side * 0.22
      pipeRun(
        lineMat[sv.line],
        [
          new THREE.Vector3(rxDrop, mainY, mainZ),
          new THREE.Vector3(rxDrop, hopY, mainZ),
          new THREE.Vector3(rxDrop, hopY, zDown),
          new THREE.Vector3(rxDrop, floorY + 1.0, zDown),
          new THREE.Vector3(rxDrop, floorY + 1.0, zUp),
          new THREE.Vector3(rxDrop, ceilY + COFFER.depth, zUp),
        ],
        sv.r * 0.8,
      )
      // Isolation valves staggered up the down-leg so no handwheel fouls its neighbour.
      if (sv.valved) valve(lineMat[sv.line], rxDrop, floorY + 1.45 + (i % 3) * 0.28, zDown, sv.r * 0.8, 'y')
    })
    if (drop.services.includes('hvac')) {
      const ductZ = rack.z + side * 0.4
      const dx = drop.x + RISER_DX.hvac
      ductRun(
        [
          new THREE.Vector3(dx, upperY + 0.24, ductZ),
          new THREE.Vector3(dx, upperY + 0.24, drop.z),
          new THREE.Vector3(dx, ceilY + COFFER.depth, drop.z),
        ],
        0.38,
        0.38,
      )
      // The damper quadrant on the branch, and the fire damper at the slab.
      add(cyl(0.11, 0.03, 12), red, dx + 0.22, upperY + 0.24, drop.z - side * 0.2).rotation.z = Math.PI / 2
      fireDamper(dx, drop.z, 0.38, 0.38, side > 0 ? -1 : 1)
    }
    // Where the bundle meets the slab: a drip tray and a bollard each end.
    //
    // WITH A RIM, which is the whole of what makes it a tray. The pan alone
    // read as a plate someone had left on the floor - and a plate holds
    // nothing. The rim gives it a capacity, so a valve gland weeping over a
    // shift ends up in a puddle under the joint that made it instead of
    // spreading across the aisle, and the puddle is where a technician
    // already looks. 50 mm, low enough to sweep out, high enough to hold a
    // night's worth of what a flanged PCW joint gives up.
    {
      const tw = DROP_FOOTPRINT.maxDx - DROP_FOOTPRINT.minDx
      const td = DROP_FOOTPRINT.halfDz * 2
      const tx = drop.x + (DROP_FOOTPRINT.maxDx + DROP_FOOTPRINT.minDx) / 2
      add(box(tw, 0.06, td), galv, tx, floorY + 0.03, drop.z)
      const rim = 0.05
      for (const sx of [-1, 1]) add(box(0.02, rim, td), galv, tx + sx * (tw / 2 - 0.01), floorY + 0.06 + rim / 2, drop.z)
      for (const sz of [-1, 1]) add(box(tw, rim, 0.02), galv, tx, floorY + 0.06 + rim / 2, drop.z + sz * (td / 2 - 0.01))
      // NO LEAK SENSOR ON THIS ONE, deliberately. The chemistry cabinets
      // have a puck in their tray because a chemical leak is a fault the
      // house model carries and the dialog can name. What weeps into this
      // tray is cooling water, which nothing in the model watches - so an
      // instrumented tray here would be a lamp wired to nothing, or worse,
      // one that lit for somebody else's leak. It is emptied by whoever
      // finds it, which is what the rim is for.
    }
    for (const dx of [DROP_FOOTPRINT.minDx - 0.15, DROP_FOOTPRINT.maxDx + 0.15]) {
      add(cyl(0.06, 0.8, 10), walkway, drop.x + dx, floorY + 0.4, drop.z - side * DROP_FOOTPRINT.halfDz)
    }
    // The placard, facing the walkway, on the walkway-end bollard at eye
    // height - beside the valve station, not in front of it.
    const pocX = drop.x + DROP_FOOTPRINT.minDx - 0.15
    const poc = new THREE.Mesh(new THREE.PlaneGeometry(0.62, 0.5425), new THREE.MeshBasicMaterial({ map: pocTexture(drop) }))
    poc.position.set(pocX, floorY + 1.35, drop.z - side * (DROP_FOOTPRINT.halfDz + 0.02))
    poc.rotation.y = side > 0 ? Math.PI : 0
    g.add(poc)
    add(box(0.04, 0.8, 0.04), strut, pocX, floorY + 1.2, drop.z - side * DROP_FOOTPRINT.halfDz)
  }

  // ------------------------------------------------------------- the skids
  const beacons = new Map<HouseSystem, THREE.MeshStandardMaterial>()
  const displays = new Map<HouseSystem, { canvas: HTMLCanvasElement; texture: THREE.CanvasTexture; last: string }>()
  const mauDisplay = ((): { canvas: HTMLCanvasElement; texture: THREE.CanvasTexture; last: string } => {
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 96
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    return { canvas, texture, last: '' }
  })()
  const fans: THREE.Object3D[] = []
  const triggers: THREE.Mesh[] = []

  /** A skid's local panel: cabinet, live readout, pilot lights. */
  const localPanel = (system: HouseSystem, x: number, z: number, out: 1 | -1): void => {
    add(box(0.5, 0.85, 0.2), panelGrey, x, floorY + 1.05, z)
    add(box(0.4, 0.3, 0.01), panelDark, x, floorY + 1.18, z + out * 0.1)
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 96
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.36, 0.135), new THREE.MeshBasicMaterial({ map: texture }))
    screen.position.set(x, floorY + 1.18, z + out * 0.106)
    screen.rotation.y = out > 0 ? 0 : Math.PI
    g.add(screen)
    displays.set(system, { canvas, texture, last: '' })
    const lights = [pilotGreen, pilotGreen, pilotAmber]
    lights.forEach((mat, i) => {
      add(cyl(0.02, 0.02, 8), mat, x - 0.12 + i * 0.12, floorY + 0.9, z + out * 0.105).rotation.x = Math.PI / 2
    })
    // A big red mushroom stop below the lights.
    add(cyl(0.035, 0.03, 10), red, x + 0.12, floorY + 0.72, z + out * 0.11).rotation.x = Math.PI / 2
  }

  const buildSkid = (skid: Skid): void => {
    const [x, z] = skid.pos
    const [w, d] = skid.size
    const colour = pipeMat[skid.system]
    const out: 1 | -1 = skid.facing > 0 ? -1 : 1
    const frontZ = z + out * (d / 2)
    // The base: a channel frame on vibration feet, decked in chequer.
    add(box(w, 0.12, d), steel, x, floorY + 0.14, z)
    for (const [dx, dz] of [
      [-w / 2 + 0.2, -d / 2 + 0.2],
      [w / 2 - 0.2, -d / 2 + 0.2],
      [-w / 2 + 0.2, d / 2 - 0.2],
      [w / 2 - 0.2, d / 2 - 0.2],
    ]) {
      add(box(0.18, 0.08, 0.18), black, x + dx, floorY + 0.04, z + dz)
    }
    add(box(w + 0.02, 0.03, 0.05), walkway, x, floorY + 0.215, frontZ)

    if (skid.kind === 'pumps') {
      // A row of roughing pumps, and the shared header they all feed - the
      // pipe whose disturbed flange is the headerLeak fault.
      const header = floorY + 2.3
      const headerEnd = x + w / 2 + 3
      pipe(colour, new THREE.Vector3(x - w / 2, header, z), new THREE.Vector3(headerEnd, header, z), 0.11)
      for (let hx = x - w / 2 + 2; hx < headerEnd; hx += 4) flangeX(steel, hx, header, z, 0.11)
      // The header is the rack's house-vacuum line, brought down to the
      // pumps: up off its end, across under the slab, and onto the rack.
      {
        const sv = lineOf('vacuum')
        const rack = PIPE_RACKS[0]
        pipeRun(
          colour,
          [
            new THREE.Vector3(headerEnd, header, z),
            new THREE.Vector3(headerEnd, HOP_Y, z),
            new THREE.Vector3(headerEnd, HOP_Y, rack.z + sv.dz),
            new THREE.Vector3(headerEnd, lineY(sv), rack.z + sv.dz),
          ],
          0.11,
        )
      }
      const n = 4
      for (let i = 0; i < n; i++) {
        const px = x - w / 2 + ((i + 0.5) * w) / n
        add(box(1.15, 0.9, 1.3), pumpBody, px, floorY + 0.65, z)
        add(cyl(0.28, 0.7, 12), steel, px, floorY + 1.3, z).rotation.z = Math.PI / 2
        // The fan cowl on the motor's free end, with the fan inside it - the
        // one part of a pump you can SEE running.
        add(cyl(0.3, 0.06, 14), black, px + 0.4, floorY + 1.3, z).rotation.z = Math.PI / 2
        const fan = new THREE.Group()
        for (const a of [0, Math.PI / 3, (2 * Math.PI) / 3]) {
          const blade = new THREE.Mesh(box(0.02, 0.5, 0.06), panelGrey)
          blade.rotation.x = a
          fan.add(blade)
        }
        fan.position.set(px + 0.44, floorY + 1.3, z)
        fan.userData.spinAxis = 'x'
        fan.userData.spinRate = 26
        fans.push(fan)
        g.add(fan)
        // Inlet from the header, with the foreline isolation valve; the
        // exhaust silencer out the back.
        pipe(colour, new THREE.Vector3(px, floorY + 1.1, z), new THREE.Vector3(px, header, z), 0.1)
        valve(colour, px, floorY + 1.85, z, 0.1, 'y')
        add(cyl(0.12, 0.5, 10), steel, px - 0.35, floorY + 1.45, z - out * 0.5)
        pipe(steel, new THREE.Vector3(px - 0.35, floorY + 1.7, z - out * 0.5), new THREE.Vector3(px - 0.35, floorY + 1.7, z - out * 1.1), 0.05)
        // Oil sight glass on the front.
        add(cyl(0.05, 0.02, 10), white, px + 0.3, floorY + 0.5, frontZ + out * 0.01).rotation.x = Math.PI / 2
      }
      gauge(x - w / 2 + 1.0, floorY + 2.0, z + out * 0.2, out)
      gauge(x + w / 2 + 1.5, header - 0.35, z + out * 0.2, out)
    } else if (skid.kind === 'chiller') {
      // A water-cooled screw chiller, which is the only kind that can live
      // indoors: nothing here blows heat into the room. Two shell-and-tube
      // heat exchangers side by side - the insulated evaporator the process
      // water leaves cold, the bare condenser the tower water carries the
      // heat away from - with the compressor on top between them, and its
      // control cabinet on the end the aisle sees. There are no fans: the
      // heat goes out of the building in pipe, not into this room in air.
      const shellLen = d - 0.6
      const evapX = x - 0.6
      const condX = x + 0.6
      const shellY = floorY + 0.9
      const insulated = std(0x2b2f36, 0.85)
      for (const [sx, m] of [
        [evapX, insulated],
        [condX, steel],
      ] as Array<[number, THREE.Material]>) {
        add(cyl(0.55, shellLen, 20), m, sx, shellY, z).rotation.x = Math.PI / 2
        for (const dz of [-shellLen / 2, shellLen / 2]) add(cyl(0.6, 0.06, 20), steel, sx, shellY, z + dz).rotation.x = Math.PI / 2
        // Saddles onto the base frame.
        for (const dz of [-1.2, 1.2]) add(box(1.1, 0.35, 0.3), black, sx, floorY + 0.35, z + dz)
      }
      // The compressor and its motor, bridged across the two shells.
      add(box(2.0, 0.12, 1.4), frameMatDark, x, floorY + 1.55, z)
      add(cyl(0.34, 1.2, 14), std(0x3c4a5c, 0.55, 0.3), x, floorY + 1.95, z + 0.2).rotation.x = Math.PI / 2
      add(cyl(0.26, 0.7, 14), black, x, floorY + 1.95, z - 0.75).rotation.x = Math.PI / 2
      // The oil separator standing beside it, and the refrigerant lines
      // between the three: discharge to the condenser, liquid to the
      // evaporator, suction back.
      add(cyl(0.2, 0.9, 12), steel, x + 0.25, floorY + 2.3, z + 1.2)
      pipe(steel, new THREE.Vector3(x, floorY + 2.1, z + 0.8), new THREE.Vector3(x + 0.25, floorY + 2.5, z + 1.2), 0.05)
      pipe(steel, new THREE.Vector3(x + 0.25, floorY + 1.85, z + 1.2), new THREE.Vector3(condX, floorY + 1.45, z + 1.2), 0.05)
      pipe(insulated, new THREE.Vector3(evapX, floorY + 1.45, z - 1.2), new THREE.Vector3(x - 0.2, floorY + 1.75, z - 1.2), 0.07)
      // The control cabinet on the aisle end, carrying the readouts.
      add(box(1.5, 2.0, 0.3), panelGrey, x + 0.45, floorY + 1.15, frontZ - out * 0.16)
      add(box(1.3, 1.7, 0.012), mccDoor, x + 0.45, floorY + 1.15, frontZ - out * 0.005)
      gauge(x + 0.1, floorY + 1.55, frontZ + out * 0.02, out, 0.07)
      gauge(x + 0.45, floorY + 1.55, frontZ + out * 0.02, out, 0.07)
      gauge(x + 0.8, floorY + 1.55, frontZ + out * 0.02, out, 0.07)
      stencil('EVAP IN · EVAP OUT · COND', x + 0.45, floorY + 1.3, frontZ + out * 0.01, out > 0 ? 'z+' : 'z-', 0.9)

      // Primary pumps beside the skid: one pushes chilled water out to the
      // rack's supply line, the other takes the rack's return back into the
      // evaporator. Every branch crosses the rack the way the drops do -
      // over the lower tier, under the trunk.
      const pumpX = x + w / 2 + 0.6
      add(box(1.4, 0.15, 3.0), steel, pumpX, floorY + 0.075, z)
      for (const dz of [-1.4, 1.4]) {
        add(cyl(0.3, 0.7, 12), steel, pumpX - 0.2, floorY + 0.5, z + dz).rotation.z = Math.PI / 2
        add(cyl(0.2, 0.6, 12), black, pumpX + 0.4, floorY + 0.5, z + dz).rotation.z = Math.PI / 2
      }
      const sup = lineOf('pcw', 'supply')
      const ret = lineOf('pcw', 'return')
      const rack = PIPE_RACKS[1]
      const nozzleY = floorY + 1.45
      const overY = floorY + 2.2
      // Evaporator outlet -> supply pump -> rack supply.
      pipeRun(colour, [new THREE.Vector3(evapX, nozzleY, z - 1.4), new THREE.Vector3(evapX, overY, z - 1.4), new THREE.Vector3(pumpX - 0.2, overY, z - 1.4), new THREE.Vector3(pumpX - 0.2, floorY + 0.8, z - 1.4)], 0.08)
      pipeRun(colour, [new THREE.Vector3(pumpX + 0.2, floorY + 0.8, z - 1.4), new THREE.Vector3(pumpX + 0.2, HOP_Y, z - 1.4), new THREE.Vector3(pumpX + 0.2, HOP_Y, rack.z + sup.dz), new THREE.Vector3(pumpX + 0.2, lineY(sup), rack.z + sup.dz)], 0.08)
      // Rack return -> return pump -> evaporator inlet.
      pipeRun(colour, [new THREE.Vector3(pumpX + 0.2, lineY(ret), rack.z + ret.dz), new THREE.Vector3(pumpX + 0.2, HOP_Y, rack.z + ret.dz), new THREE.Vector3(pumpX + 0.2, HOP_Y, z + 1.4), new THREE.Vector3(pumpX + 0.2, floorY + 0.8, z + 1.4)], 0.08)
      pipeRun(colour, [new THREE.Vector3(pumpX - 0.2, floorY + 0.8, z + 1.4), new THREE.Vector3(pumpX - 0.2, overY, z + 1.4), new THREE.Vector3(evapX, overY, z + 1.4), new THREE.Vector3(evapX, nozzleY, z + 1.4)], 0.08)
      valve(colour, pumpX + 0.2, floorY + 1.5, z - 1.4, 0.08, 'y')
      valve(colour, pumpX + 0.2, floorY + 1.5, z + 1.4, 0.08, 'y')

      // The condenser-water mains run OVERHEAD, as pipe always does where
      // people walk: up out of the condenser, the length of the room just
      // off the rack, and out through the east wall to the cooling tower.
      // Uninsulated black steel: tower water is neither cold nor clean.
      const towerMat = std(0x3a3d40, 0.6, 0.3)
      for (const [i, dz] of [-0.9, -0.5].entries()) {
        const runY = ceilY - 0.5 - i * 0.25
        pipeRun(
          towerMat,
          [
            new THREE.Vector3(condX, nozzleY, z + dz),
            new THREE.Vector3(condX, runY, z + dz),
            new THREE.Vector3(SUBFAB.maxX + 0.1, runY, z + dz),
          ],
          0.1,
        )
        // Turned the other way from the rack markers: these lines are read
        // from the walkway on their -z side. Clevis hangers every 3 m.
        for (let px = condX + 5; px < SUBFAB.maxX - 2; px += 10) {
          add(cyl(0.108, 0.5, 14), condenserLabels[i], px, runY, z + dz).rotation.z = -Math.PI / 2
        }
        for (let px = condX + 2; px < SUBFAB.maxX - 0.5; px += 3) {
          add(cyl(0.012, ceilY - runY - 0.1, 6), rod, px, (ceilY + runY + 0.1) / 2, z + dz)
          add(new THREE.TorusGeometry(0.12, 0.012, 6, 14), rod, px, runY, z + dz).rotation.y = Math.PI / 2
        }
      }
      stencil('CONDENSER WATER · TO COOLING TOWER', SUBFAB.maxX - 0.14, ceilY - 1.05, z - 0.1, 'x-', 1.4)
    } else if (skid.kind === 'dryer') {
      // The twin towers of a regenerative dryer - the one that failed wet -
      // with the purge manifold between them and the muffler on top.
      for (const dx of [-0.7, 0.7]) {
        add(cyl(0.45, 1.9, 16), steel, x + dx, floorY + 1.15, z)
        add(new THREE.SphereGeometry(0.45, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), steel, x + dx, floorY + 2.1, z)
        add(cyl(0.5, 0.06, 16), black, x + dx, floorY + 0.23, z)
        add(cyl(0.5, 0.06, 16), black, x + dx, floorY + 2.08, z)
      }
      pipe(colour, new THREE.Vector3(x - 0.7, floorY + 0.7, z), new THREE.Vector3(x + 0.7, floorY + 0.7, z), 0.06)
      pipe(colour, new THREE.Vector3(x - 0.7, floorY + 2.4, z), new THREE.Vector3(x + 0.7, floorY + 2.4, z), 0.06)
      for (const dx of [-0.35, 0.35]) valve(colour, x + dx, floorY + 0.7, z, 0.06, 'x')
      // The purge muffler, venting up beside the outlet.
      add(cyl(0.12, 0.4, 12), steel, x - 0.35, floorY + 2.62, z)
      // Dried air leaves the top manifold through the after-filter and goes
      // onto the rack's CDA line - which is where every tool's air starts.
      {
        const cda = lineOf('cda')
        const rack = PIPE_RACKS[0]
        const ox = x - 1.35
        pipe(colour, new THREE.Vector3(x - 0.7, floorY + 2.4, z), new THREE.Vector3(ox, floorY + 2.4, z), 0.06)
        add(cyl(0.13, 0.5, 12), white, ox, floorY + 2.4, z - 0.4).rotation.x = Math.PI / 2
        pipeRun(
          colour,
          [
            new THREE.Vector3(ox, floorY + 2.4, z),
            new THREE.Vector3(ox, floorY + 2.4, z - 0.6),
            new THREE.Vector3(ox, HOP_Y, z - 0.6),
            new THREE.Vector3(ox, HOP_Y, rack.z + cda.dz),
            new THREE.Vector3(ox, lineY(cda), rack.z + cda.dz),
          ],
          0.06,
        )
      }
      // Inlet filter on the wet side, and the dew-point sensor panel on the
      // dry side, clear of the name plate.
      add(cyl(0.16, 0.6, 12), white, x - 1.85, floorY + 1.4, z)
      // ...out of its bottom and into the towers' inlet manifold.
      pipeRun(colour, [new THREE.Vector3(x - 1.85, floorY + 1.1, z), new THREE.Vector3(x - 1.85, floorY + 0.7, z), new THREE.Vector3(x - 0.7, floorY + 0.7, z)], 0.06)
      add(box(0.45, 0.5, 0.12), panelGrey, x + 1.3, floorY + 1.5, frontZ + out * 0.04)
      gauge(x + 1.18, floorY + 1.52, frontZ + out * 0.1, out, 0.07)
      gauge(x + 1.42, floorY + 1.52, frontZ + out * 0.1, out, 0.07)
      stencil('DEW POINT', x + 1.3, floorY + 1.3, frontZ + out * 0.105, out > 0 ? 'z+' : 'z-', 0.36)
    } else if (skid.kind === 'upw') {
      // RO housings racked horizontally, the two polishing mixed-bed columns
      // (the ones that exhaust), the UV oxidiser between them - and the DI
      // storage tank the loop draws from.
      for (let i = 0; i < 3; i++) {
        add(cyl(0.22, 2.6, 12), white, x - 0.8, floorY + 0.6 + i * 0.55, z).rotation.z = Math.PI / 2
        for (const dx of [-1.1, 1.1]) add(cyl(0.26, 0.08, 12), steel, x - 0.8 + dx, floorY + 0.6 + i * 0.55, z).rotation.z = Math.PI / 2
      }
      add(box(0.08, 1.9, 0.08), strut, x - 2.1, floorY + 1.1, z - 0.3)
      add(box(0.08, 1.9, 0.08), strut, x + 0.5, floorY + 1.1, z - 0.3)
      // What the horizontal housings are: the polish loop's ultrafilters,
      // the last barrier before the water reaches a tool - not RO, which
      // belongs to the primary plant, not a sub-fab.
      stencil('ULTRAFILTERS', x - 1.5, floorY + 2.15, frontZ + out * 0.03, out > 0 ? 'z+' : 'z-', 0.8)
      stencil('UV OXIDISER', x + 0.8, floorY + 0.66, z - (skid.facing * d) / 4 + out * 0.19, out > 0 ? 'z+' : 'z-', 0.5)
      const bedMat = std(0x3f7d68, 0.5)
      for (const dx of [1.4, 2.4]) {
        const bx = x - w / 2 + dx + 0.8
        const bz = z + (skid.facing * d) / 4
        add(cyl(0.38, 1.9, 16), bedMat, bx, floorY + 1.15, bz)
        add(new THREE.SphereGeometry(0.38, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), bedMat, bx, floorY + 2.1, bz)
        valve(colour, bx, floorY + 2.55, bz, 0.05, 'y')
      }
      add(box(1.4, 0.35, 0.35), steel, x + 0.8, floorY + 0.55, z - (skid.facing * d) / 4)
      add(cyl(0.05, 1.5, 8), sightGlass, x + 0.8, floorY + 0.55, z - (skid.facing * d) / 4 + 0.2).rotation.z = Math.PI / 2
      // The storage tank, with its level sight tube. Its dome stops a hand
      // under the slab - the room is 3.25 m to the waffle.
      const tx = x + w / 2 - 0.85
      add(cyl(0.7, 1.9, 20), white, tx, floorY + 1.1, z)
      add(new THREE.SphereGeometry(0.7, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2), white, tx, floorY + 2.05, z)
      add(cyl(0.74, 0.05, 20), steel, tx, floorY + 0.23, z)
      add(cyl(0.03, 2.0, 8), sightGlass, tx + out * 0.03, floorY + 1.35, z + out * 0.74)
      // Nitrogen blanket on the tank: the headspace over stored UPW is kept
      // under nitrogen, or the water picks up CO2 from the air and its
      // resistivity falls before it ever reaches a tool.
      {
        const n2 = lineOf('n2')
        const rack = PIPE_RACKS[1]
        const bx = tx - 0.3
        pipeRun(
          lineMat.n2,
          [
            new THREE.Vector3(bx, lineY(n2), rack.z + n2.dz),
            new THREE.Vector3(bx, HOP_Y, rack.z + n2.dz),
            new THREE.Vector3(bx, HOP_Y, z - 1.0),
            new THREE.Vector3(bx, floorY + 2.0, z - 1.0),
            new THREE.Vector3(bx, floorY + 2.0, z - 0.62),
          ],
          0.012,
        )
        valve(lineMat.n2, bx, floorY + 2.25, z - 1.0, 0.014, 'y')
        stencil('N2 BLANKET', bx, floorY + 1.75, z - 0.64, 'z-', 0.4)
      }
      pipe(colour, new THREE.Vector3(x - w / 2, floorY + 2.25, z), new THREE.Vector3(tx, floorY + 2.25, z), 0.1)
      // The polish loop: supply up and over onto the rack's UPW line, and
      // the return back off the rack into the side of the storage tank - a
      // loop never dead-ends, because standing water grows things.
      {
        const sup = lineOf('upw', 'supply')
        const ret = lineOf('upw', 'return')
        const rack = PIPE_RACKS[1]
        pipeRun(colour, [new THREE.Vector3(x + 0.8, floorY + 2.25, z), new THREE.Vector3(x + 0.8, HOP_Y, z), new THREE.Vector3(x + 0.8, HOP_Y, rack.z + sup.dz), new THREE.Vector3(x + 0.8, lineY(sup), rack.z + sup.dz)], 0.06)
        pipeRun(
          colour,
          [
            new THREE.Vector3(tx, lineY(ret), rack.z + ret.dz),
            new THREE.Vector3(tx, HOP_Y, rack.z + ret.dz),
            new THREE.Vector3(tx, HOP_Y, z - 0.95),
            new THREE.Vector3(tx, floorY + 1.9, z - 0.95),
            new THREE.Vector3(tx, floorY + 1.9, z - 0.6),
          ],
          0.06,
        )
      }
      // Distribution pumps at the front.
      for (const dx of [-0.2, 0.5]) {
        add(cyl(0.18, 0.55, 12), steel, x + dx, floorY + 0.42, frontZ - out * 0.35).rotation.z = Math.PI / 2
      }
      gauge(x - 1.9, floorY + 1.6, frontZ + out * 0.03, out, 0.07)
      gauge(x + 0.25, floorY + 1.6, frontZ + out * 0.03, out, 0.07)
    } else {
      // AHUs: two big galvanised boxes with filter doors, belt housings and
      // duct risers - the belts that slip are inside these.
      for (const dx of [-w / 4, w / 4]) {
        const uw = w / 2 - 0.5
        add(box(uw, 2.1, d), ahuSkin, x + dx, floorY + 1.2, z)
        // Access doors along the front, each with a handle; the filter
        // section's door carries the differential-pressure gauge.
        for (let i = 0; i < 3; i++) {
          const doorX = x + dx - uw / 2 + 0.35 + i * ((uw - 0.7) / 2)
          add(box(1.0, 1.6, 0.02), ahuDoor, doorX, floorY + 1.2, frontZ + out * 0.01)
          add(box(0.04, 0.22, 0.06), black, doorX + 0.42, floorY + 1.2, frontZ + out * 0.04)
        }
        gauge(x + dx - uw / 2 + 0.35, floorY + 2.05, frontZ + out * 0.03, out, 0.07)
        add(box(0.5, 0.7, 0.25), concreteDark, x + dx, floorY + 1.05, z - out * (d / 2 + 0.13))
        add(box(1.1, Math.abs(ceilY - (floorY + 2.25)), 1.1), galv, x + dx, (ceilY + floorY + 2.25) / 2, z)
        fireDamper(x + dx, z, 1.1, 1.1)
        stencil('SUPPLY AIR ↑', x + dx, floorY + 2.62, z + out * 0.556, out > 0 ? 'z+' : 'z-', 0.9)
        // The flexible connector at the unit's outlet: a black band.
        add(box(1.16, 0.22, 1.16), black, x + dx, floorY + 2.42, z)
        // The motor on top, driving the belt housing.
        add(cyl(0.2, 0.5, 12), black, x + dx + 0.5, floorY + 2.42, z - out * (d / 2 - 0.3)).rotation.z = Math.PI / 2
        // A recirculation unit carries a sensible cooling coil - the tools'
        // heat has to leave the loop somewhere - fed from the rack's chilled
        // water, supply and return, down into the coil section's top.
        {
          const rack = PIPE_RACKS[1]
          for (const sv of [lineOf('pcw', 'supply'), lineOf('pcw', 'return')]) {
            const px = x + dx - 1.5 + (sv.role === 'return' ? 0.24 : 0)
            pipeRun(
              lineMat.pcw,
              [
                new THREE.Vector3(px, lineY(sv), rack.z + sv.dz),
                new THREE.Vector3(px, HOP_Y, rack.z + sv.dz),
                new THREE.Vector3(px, HOP_Y, z - out * 0.9),
                new THREE.Vector3(px, floorY + 2.2, z - out * 0.9),
              ],
              0.05,
            )
          }
          valve(lineMat.pcw, x + dx - 1.5, floorY + 2.5, z - out * 0.9, 0.05, 'y')
          stencil('COOLING COIL', x + dx - 1.5, floorY + 1.95, frontZ + out * 0.03, out > 0 ? 'z+' : 'z-', 0.6)
        }
      }
      // The return-air intake between the units: the handlers breathe the
      // plenum this room IS, through a louvred face.
      add(box(0.6, 1.4, d - 0.6), galv, x, floorY + 1.0, z)
      louvreTex.repeat.set(1, 8)
      const intake = add(new THREE.PlaneGeometry(0.5, 1.2), louvre, x, floorY + 1.0, z + out * ((d - 0.6) / 2 + 0.005))
      intake.rotation.y = out > 0 ? 0 : Math.PI
      stencil('RETURN', x, floorY + 1.78, z + out * ((d - 0.6) / 2 + 0.006), out > 0 ? 'z+' : 'z-', 0.45)
    }

    // The placard, the beacon and the local panel every skid carries, facing
    // the service aisle.
    const placard = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 0.4), new THREE.MeshBasicMaterial({ map: placardTexture(skid.system) }))
    placard.position.set(x, floorY + 2.05, frontZ + out * 0.03)
    placard.rotation.y = out > 0 ? 0 : Math.PI
    g.add(placard)

    const beaconMat = new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x1f8f4a, emissiveIntensity: 0.9, roughness: 0.4 })
    const beacon = new THREE.Mesh(cyl(0.07, 0.16, 10), beaconMat)
    beacon.position.set(x + 0.7, floorY + 2.36, frontZ + out * 0.02)
    g.add(beacon)
    add(cyl(0.02, 0.5, 6), steel, x + 0.7, floorY + 2.05, frontZ + out * 0.02)
    beacons.set(skid.system, beaconMat)

    localPanel(skid.system, x - 1.3, frontZ + out * 0.12, out)

    // The click volume: the whole skid, waist to beacon, invisible.
    const trigger = new THREE.Mesh(box(w + 0.4, 2.5, d + 0.4), new THREE.MeshBasicMaterial({ visible: false }))
    trigger.position.set(x, floorY + 1.25, z)
    trigger.userData.skidSystem = skid.system
    triggers.push(trigger)
    g.add(trigger)
  }
  for (const skid of SKIDS) buildSkid(skid)

  // ------------------------------------------------------- the switchgear
  //
  // The MCC lineup on the north wall, its wireway and the conduits that
  // leave it, and the painted clearance in front.
  {
    const n = Math.round((MCC.maxX - MCC.minX) / 0.92)
    const cabW = (MCC.maxX - MCC.minX) / n
    const face = MCC.z + MCC.depth / 2
    for (let i = 0; i < n; i++) {
      const x = MCC.minX + (i + 0.5) * cabW
      add(box(cabW - 0.03, 2.25, MCC.depth), panelGrey, x, floorY + 1.125, MCC.z)
      add(box(cabW - 0.16, 1.95, 0.015), mccDoor, x, floorY + 1.18, face + 0.008)
      add(box(0.035, 0.18, 0.05), black, x + cabW / 2 - 0.13, floorY + 1.2, face + 0.03)
      add(box(cabW - 0.3, 0.09, 0.01), black, x, floorY + 2.02, face + 0.017)
      gauge(x - 0.15, floorY + 1.75, face + 0.02, 1, 0.06)
      ;[pilotGreen, pilotRed].forEach((mat, j) => {
        add(cyl(0.018, 0.02, 8), mat, x + 0.1 + j * 0.1, floorY + 1.75, face + 0.02).rotation.x = Math.PI / 2
      })
    }
    add(box(MCC.maxX - MCC.minX + 0.2, 0.22, 0.3), panelGrey, (MCC.minX + MCC.maxX) / 2, floorY + 2.36, MCC.z)
    // Conduits up the wall and along it, to the racks and the skids.
    const wallZ = SUBFAB.minZ + 0.2
    for (const [i, dx] of [-1.2, -0.6, 0.0, 0.6, 1.2].entries()) {
      const cx0 = (MCC.minX + MCC.maxX) / 2 + dx
      // Under the downstand beams, which cross the wall on the column lines.
      const yRun = ceilY - 0.45 - (i % 2) * 0.08
      pipeRun(
        conduitMat,
        [
          new THREE.Vector3(cx0, floorY + 2.47, wallZ),
          new THREE.Vector3(cx0, yRun, wallZ),
          new THREE.Vector3(i < 2 ? SUBFAB.minX + 2 : i > 2 ? SUBFAB.maxX - 2 : cx0, yRun, wallZ),
        ],
        0.025,
      )
    }
    // The painted clearance: a hatched rectangle, and the sign that names it.
    const kc = add(new THREE.PlaneGeometry(MCC.maxX - MCC.minX + 0.4, MCC.clearance), hazard, (MCC.minX + MCC.maxX) / 2, floorY + 0.005, face + MCC.clearance / 2 + 0.05)
    kc.rotation.x = -Math.PI / 2
    ;(hazard.map as THREE.Texture).repeat.set(6, 1)
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(0.6, 0.375),
      new THREE.MeshBasicMaterial({ map: signTexture(['DANGER', '480 V', 'KEEP CLEAR'], '#f2f4f6', '#b3261e') }),
    )
    sign.position.set((MCC.minX + MCC.maxX) / 2, floorY + 2.75, wallZ + 0.01)
    g.add(sign)
  }

  // ---------------------------------------------------- housekeeping pads
  //
  // Plant does not sit on the slab. It sits on a 100 mm pad poured over it,
  // a hand's breadth bigger than the machine all round, with the anchor
  // bolts cast in - which keeps a leak or a wash-down off the machine's
  // feet, gives the anchors something to bite that is not the structural
  // slab, and is the difference between a machine that was INSTALLED and a
  // machine that was put down.
  //
  // Not everything gets one, and what it is standing in says which: the
  // chemistry cabinets sit in leak trays, the drum pallet is its own bund,
  // and an eyewash post is bolted straight down. The pads sit a few
  // millimetres proud of the slab so the wear decal, which lies at 7 mm,
  // passes under them rather than through their faces.
  {
    const padded: Array<{ x: number; z: number; w: number; d: number }> = [
      ...SKIDS.map((s) => ({ x: s.pos[0], z: s.pos[1], w: s.size[0], d: s.size[1] })),
      ...SUPPORT_GEAR.filter((gr) => ['dryPump', 'abatement', 'receiver', 'vacReceiver', 'awn', 'supportBank'].includes(gr.kind)).map(
        (gr) => ({ x: gr.x, z: gr.z, w: gr.w, d: gr.d }),
      ),
      { x: (MAU.minX + MAU.maxX) / 2, z: MAU.z, w: MAU.maxX - MAU.minX, d: MAU.depth },
    ]
    const PAD = { over: 0.13, high: 0.1, base: 0.008 }
    for (const p of padded) {
      add(box(p.w + PAD.over * 2, PAD.high, p.d + PAD.over * 2), concreteDark, p.x, floorY + PAD.base + PAD.high / 2, p.z)
      // Anchor bolts at the corners of the machine's own footprint, which is
      // where they would be: through the frame, not through the overhang.
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          add(cyl(0.016, 0.05, 6), steel, p.x + sx * (p.w / 2 - 0.05), floorY + PAD.base + PAD.high + 0.02, p.z + sz * (p.d / 2 - 0.05))
        }
      }
    }
    // The exhaust fan's pad is round, because its plinth follows its shell.
    add(cyl(EXHAUST_PLANT.radius + 0.2, PAD.high, 20), concreteDark, EXHAUST_PLANT.x, floorY + PAD.base + PAD.high / 2, EXHAUST_PLANT.z)
  }

  // --------------------------------------------------------- power to plant
  //
  // The MCC's conduits used to leave it and stop in mid-air along the wall.
  // Every machine in this room is fed from that lineup, so the runs now go
  // somewhere: along the wall or the rack's cable tray, then a drop to a
  // local disconnect within sight of the machine it kills. That switch is
  // the point of the whole layer - a technician who is about to put a hand
  // inside a pump does not walk back to the MCC and hope.
  //
  // Two sources, whichever is nearer: the wall run at the north wall, for
  // the plant that stands against it, and the cable tray on the nearest
  // pipe rack for everything out in the room. The trays are fed from the
  // MCC too - the link is drawn below - so the whole path is walkable.
  {
    const trayY = upperY + 0.06
    const wallRunY = ceilY - 0.45
    const wallRunZ = SUBFAB.minZ + 0.2
    /** Where a machine's feed comes from, in z, and at what height. */
    const sourceFor = (z: number): { y: number; z: number } => {
      const rack = PIPE_RACKS.reduce((a, b) => (Math.abs(b.z - z) < Math.abs(a.z - z) ? b : a))
      const trayZ = rack.z - (Math.sign(rack.z) || 1) * 0.45
      return Math.abs(z - wallRunZ) < Math.abs(z - trayZ) ? { y: wallRunY, z: wallRunZ } : { y: trayY, z: trayZ }
    }
    /**
     * One machine's feed: along the source, out to the machine, down its
     * face to the disconnect. `tall` says whether the switch lands on the
     * machine itself or on a stanchion beside it, which is where it goes
     * when the machine is not tall enough to carry it at a readable height.
     */
    const feed = (x: number, z: number, w: number, d: number, facing: 1 | -1, tall: boolean): void => {
      const src = sourceFor(z)
      const faceZ = z - facing * (d / 2)
      const standoff = faceZ - facing * 0.1
      const dx = x + w / 2 - 0.3
      pipeRun(
        conduitMat,
        [
          new THREE.Vector3(dx, src.y, src.z),
          new THREE.Vector3(dx, src.y, standoff),
          new THREE.Vector3(dx, floorY + 1.72, standoff),
        ],
        0.022,
      )
      // Hangers on the horizontal leg: conduit is supported, never hung on
      // its own stiffness.
      const from = Math.min(src.z, standoff)
      const to = Math.max(src.z, standoff)
      for (let hz = from + 0.9; hz < to - 0.3; hz += 1.6) {
        add(cyl(0.007, ceilY - src.y, 6), rod, dx, (ceilY + src.y) / 2, hz)
      }
      if (!tall) add(cyl(0.03, 1.7, 8), strut, dx, floorY + 0.85, standoff)
      // The disconnect: a box, a lever, and the padlock hasp that makes a
      // lock-out possible at all.
      add(box(0.24, 0.32, 0.14), panelGrey, dx, floorY + 1.5, standoff - facing * 0.06)
      add(box(0.05, 0.12, 0.04), black, dx + 0.14, floorY + 1.56, standoff - facing * 0.06)
      add(cyl(0.012, 0.03, 6), steel, dx + 0.14, floorY + 1.44, standoff - facing * 0.06).rotation.x = Math.PI / 2
    }
    for (const s of SKIDS) feed(s.pos[0], s.pos[1], s.size[0], s.size[1], s.facing, true)
    for (const gr of SUPPORT_GEAR) {
      if (gr.kind === 'dryPump' || gr.kind === 'supportBank') feed(gr.x, gr.z, gr.w, gr.d, gr.facing, false)
      else if (gr.kind === 'abatement' || gr.kind === 'awn') feed(gr.x, gr.z, gr.w, gr.d, gr.facing, gr.kind === 'abatement')
    }
    feed((MAU.minX + MAU.maxX) / 2, MAU.z, MAU.maxX - MAU.minX, MAU.depth, 1, true)
    feed(EXHAUST_PLANT.x, EXHAUST_PLANT.z, EXHAUST_PLANT.radius * 2, EXHAUST_PLANT.radius * 2, -1, true)
    // And the link that makes the trays part of the same system: out of the
    // MCC's wall run, along to the north tray, over to the south one at a
    // clear span between columns, and down onto each.
    for (const [x, toZ] of [
      [24.4, -3.5],
      [21.4, 3.5],
    ] as Array<[number, number]>) {
      const trayZ = toZ - Math.sign(toZ) * 0.45
      const fromZ = toZ < 0 ? wallRunZ : -3.05
      pipeRun(
        conduitMat,
        [
          new THREE.Vector3(x, wallRunY, fromZ),
          new THREE.Vector3(x, wallRunY, trayZ),
          new THREE.Vector3(x, trayY + 0.04, trayZ),
        ],
        0.03,
      )
      for (let hz = Math.min(fromZ, trayZ) + 1; hz < Math.max(fromZ, trayZ) - 0.5; hz += 1.8) {
        add(cyl(0.007, ceilY - wallRunY, 6), rod, x, (ceilY + wallRunY) / 2, hz)
      }
    }
  }

  // ------------------------------------------------------- exhaust plant
  //
  // Where the trunk ducts go: the scrubber column, the fan, the stack.
  {
    const { x, z, radius } = EXHAUST_PLANT
    add(cyl(radius, 2.0, 20), frp, x, floorY + 1.1, z)
    add(new THREE.SphereGeometry(radius, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2), frp, x, floorY + 2.1, z)
    add(cyl(radius + 0.05, 0.08, 20), steel, x, floorY + 0.14, z)
    add(cyl(radius + 0.05, 0.08, 20), steel, x, floorY + 1.2, z)
    // Recirculation pump and the sump at the base.
    add(cyl(0.16, 0.5, 12), steel, x - radius - 0.4, floorY + 0.35, z).rotation.z = Math.PI / 2
    pipe(pipeMat.upw, new THREE.Vector3(x - radius - 0.4, floorY + 0.5, z), new THREE.Vector3(x - radius - 0.4, floorY + 2.4, z), 0.05)
    pipe(pipeMat.upw, new THREE.Vector3(x - radius - 0.4, floorY + 2.4, z), new THREE.Vector3(x - radius + 0.1, floorY + 2.4, z), 0.05)
    // Both trunks come here: down off the racks, into the column's side.
    for (const rack of PIPE_RACKS) {
      const side = Math.sign(rack.z) || 1
      const ductZ = rack.z + side * 0.4
      ductRun(
        [
          new THREE.Vector3(rack.maxX + 0.2, upperY + 0.24, ductZ),
          new THREE.Vector3(x - 0.3, upperY + 0.24, ductZ),
          new THREE.Vector3(x - 0.3, upperY + 0.24, z + Math.sign(ductZ - z) * radius),
          new THREE.Vector3(x - 0.3, floorY + 2.35, z + Math.sign(ductZ - z) * radius),
        ],
        0.7,
        0.42,
      )
    }
    // The fan beside the column, and its discharge out through the east
    // wall to the stack on the roof: scrubbed exhaust leaves the building,
    // it does not rise into the cleanroom.
    const fx = x + radius + 0.55
    add(box(0.9, 0.9, 0.9), galv, fx, floorY + 1.4, z)
    add(cyl(0.2, 0.6, 12), black, fx, floorY + 1.4, z + 0.75).rotation.x = Math.PI / 2
    ductRun([new THREE.Vector3(x, floorY + 2.3, z), new THREE.Vector3(fx, floorY + 2.3, z)], 0.45, 0.45)
    ductRun([new THREE.Vector3(fx, floorY + 1.85, z), new THREE.Vector3(fx, ceilY - 0.5, z), new THREE.Vector3(SUBFAB.maxX + 0.1, ceilY - 0.5, z)], 0.5, 0.5)
    stencil('TO ROOF STACK →', fx + 0.35, ceilY - 0.5, z + 0.256, 'z+', 0.8, '#f2f4f6')
    // A wet scrubber concentrates what it catches, so its sump is bled to
    // the neutralisation skid: the blowdown line, at floor level along the
    // north wall where nobody walks, into the back of the first tank.
    {
      const awn = SUPPORT_GEAR.find((gr) => gr.kind === 'awn')
      if (awn) {
        const inX = awn.x - 0.95
        const yb = floorY + 0.18
        const wallZ = SUBFAB.minZ + 0.3
        const bx = x - radius - 0.2
        pipeRun(
          frp,
          [
            new THREE.Vector3(x - radius, yb, z),
            new THREE.Vector3(bx, yb, z),
            new THREE.Vector3(bx, yb, wallZ),
            new THREE.Vector3(inX, yb, wallZ),
            new THREE.Vector3(inX, floorY + 0.9, wallZ),
            new THREE.Vector3(inX, floorY + 0.9, awn.z - 0.5),
          ],
          0.03,
        )
        valve(frp, bx, yb, z - 0.5, 0.03, 'z')
        stencil('SCRUBBER BLOWDOWN TO AWN', bx - 0.7, yb + 0.28, SUBFAB.minZ + 0.14, 'z+', 1.0)
      }
    }
    // Its instruments face the walkway, the side a technician arrives from.
    gauge(x, floorY + 1.9, z + radius + 0.02, 1)
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(0.5, 0.31),
      new THREE.MeshBasicMaterial({ map: signTexture(['PROCESS EXHAUST', 'SCRUBBER'], '#22262c', '#f2f4f6') }),
    )
    sign.position.set(x, floorY + 1.3, z + radius + 0.02)
    g.add(sign)
  }

  // ------------------------------------------------------- the air path
  //
  // The make-up air unit, section by section the way a technician learns it:
  // outside air in through the wall louvre, pre-filter, chilled-water coil,
  // hot-water reheat, humidifier, supply fan, discharge up through the slab.
  // Every section is labelled on the casing and the coils are fed from real
  // pipe - the chilled water off the rack, the hot water from above.
  {
    const { minX, maxX, z, depth } = MAU
    const yBase = floorY + 0.15
    const h = 2.1
    const yC = yBase + h / 2
    const front = z - depth / 2
    // Section order is the order the air meets them: a preheat coil ahead
    // of the cooling coil so a winter morning cannot freeze it, reheat and
    // humidifier after it to set the supply state, the fan, and a final
    // filter after the fan so the last thing the air touches is clean.
    const sections: Array<[string, number]> = [
      ['OUTSIDE AIR', 0.6],
      ['PRE-FILTER', 0.6],
      ['PRE-HEAT', 0.5],
      ['CHW COIL', 0.8],
      ['HW COIL', 0.6],
      ['HUMIDIFIER', 0.8],
      ['SUPPLY FAN', 1.5],
      ['FINAL FILTER', 0.9],
      ['DISCHARGE', 0.7],
    ]
    add(box(maxX - minX, 0.15, depth), steel, (minX + maxX) / 2, floorY + 0.075, z)
    const centre: Record<string, number> = {}
    let x = maxX
    for (const [label, len] of sections) {
      const cx0 = x - len / 2
      centre[label] = cx0
      add(box(len - 0.02, h, depth), casing, cx0, yC, z)
      add(box(0.03, h + 0.02, depth + 0.02), strut, x - len, yC, z)
      stencil(label, cx0, yBase + h - 0.22, front - 0.012, 'z-', Math.min(len - 0.1, 1.2))
      x -= len
    }
    // Access doors on the filter and fan sections, with the filter's
    // differential-pressure gauge beside its door.
    for (const key of ['PRE-FILTER', 'SUPPLY FAN']) {
      const dx = centre[key]
      add(box(key === 'SUPPLY FAN' ? 0.9 : 0.5, 1.5, 0.02), mccDoor, dx, yBase + 0.95, front - 0.01)
      add(box(0.04, 0.2, 0.06), black, dx + (key === 'SUPPLY FAN' ? 0.38 : 0.18), yBase + 0.95, front - 0.04)
    }
    gauge(centre['PRE-FILTER'] - 0.28, yBase + 1.75, front - 0.03, -1, 0.07)
    gauge(centre['CHW COIL'], yBase + 1.75, front - 0.03, -1, 0.07)
    // The fan, visible through its inlet cone: the one moving part.
    {
      const fx = centre['SUPPLY FAN']
      add(cyl(0.42, 0.04, 20), black, fx, yBase + 1.0, front - 0.03).rotation.x = Math.PI / 2
      add(new THREE.TorusGeometry(0.42, 0.03, 8, 24), strut, fx, yBase + 1.0, front - 0.06)
      const fan = new THREE.Group()
      for (let i = 0; i < 6; i++) {
        const blade = new THREE.Mesh(box(0.7, 0.09, 0.015), panelGrey)
        blade.rotation.z = (i * Math.PI) / 3
        fan.add(blade)
      }
      fan.position.set(fx, yBase + 1.0, front - 0.06)
      fan.userData.spinAxis = 'z'
      fan.userData.spinRate = 18
      fans.push(fan)
      g.add(fan)
      const motor = add(cyl(0.16, 0.5, 12), black, fx + 0.75, yBase + h + 0.25, z)
      motor.rotation.z = Math.PI / 2
    }
    // Chilled water to the cooling coil, off the rack's supply and return
    // lines: up over the rest of the tier, across the room, down into the
    // coil section's top.
    {
      const rack = PIPE_RACKS[1]
      const hopY = HOP_Y
      SERVICES.filter((sv) => sv.system === 'pcw').forEach((sv, i) => {
        const px = centre['CHW COIL'] - 0.15 + i * 0.3
        const mainY = lowerY + 0.0225 + sv.r
        pipeRun(
          pipeMat.pcw,
          [
            new THREE.Vector3(px, mainY, rack.z + sv.dz),
            new THREE.Vector3(px, hopY, rack.z + sv.dz),
            new THREE.Vector3(px, hopY, z - 0.5),
            new THREE.Vector3(px, yBase + h - 0.05, z - 0.5),
          ],
          0.06,
        )
      })
      // Heating hot water and steam come in through the wall the unit backs
      // onto - the boiler plant is not this room's, and nothing but a tool
      // service ever rises through the cleanroom floor - and drop into their
      // sections from a short run under the slab.
      for (const [dx, mat, r] of [
        [-0.12, hotWater, 0.045],
        [0.12, hotWater, 0.045],
        [centre['PRE-HEAT'] - centre['HW COIL'] - 0.1, hotWater, 0.045],
        [centre['PRE-HEAT'] - centre['HW COIL'] + 0.1, hotWater, 0.045],
        [centre['HUMIDIFIER'] - centre['HW COIL'], white, 0.035],
      ] as Array<[number, THREE.Material, number]>) {
        const px = centre['HW COIL'] + dx
        pipeRun(
          mat,
          [
            new THREE.Vector3(px, yBase + h - 0.05, z - 0.4),
            new THREE.Vector3(px, ceilY - 0.2, z - 0.4),
            new THREE.Vector3(px, ceilY - 0.2, SUBFAB.maxZ + 0.1),
          ],
          r,
        )
      }
      stencil('HHW', centre['HW COIL'], yBase + h + 0.35, z - 0.4 - 0.06, 'z-', 0.35, '#f2f4f6')
      stencil('STEAM', centre['HUMIDIFIER'], yBase + h + 0.35, z - 0.4 - 0.05, 'z-', 0.4)
      // The coil's condensate, trapped and run to a floor drain.
      pipe(white, new THREE.Vector3(centre['CHW COIL'] + 0.35, yBase + 0.3, front - 0.05), new THREE.Vector3(centre['CHW COIL'] + 0.35, floorY + 0.06, front - 0.05), 0.02)
      pipe(white, new THREE.Vector3(centre['CHW COIL'] + 0.35, floorY + 0.06, front - 0.05), new THREE.Vector3(centre['CHW COIL'] + 0.35, floorY + 0.06, front - 0.45), 0.02)
      add(box(0.3, 0.02, 0.3), grating, centre['CHW COIL'] + 0.35, floorY + 0.006, front - 0.55)
    }
    // Discharge: up through the slab, behind its fire damper.
    {
      const dx = centre['DISCHARGE']
      ductRun([new THREE.Vector3(dx, yBase + h, z), new THREE.Vector3(dx, ceilY + COFFER.depth, z)], 0.65, 0.8)
      fireDamper(dx, z, 0.65, 0.65)
      stencil('SUPPLY AIR ↑', dx, yBase + h + 0.38, z - 0.331, 'z-', 0.6)
      stencil('TO CEILING PLENUM', dx, yBase + h + 0.22, z - 0.331, 'z-', 0.6)
    }
    // Outside air: the wall louvre, its motorised damper and the intake duct.
    {
      const iy = floorY + 1.4
      louvreTex.repeat.set(1, 8)
      const wallX = SUBFAB.maxX - 0.14
      const grille = add(new THREE.PlaneGeometry(1.1, 1.1), louvre, wallX, iy, z)
      grille.rotation.y = -Math.PI / 2
      add(box(0.05, 1.2, 1.2), strut, wallX + 0.02, iy, z)
      ductRun([new THREE.Vector3(wallX - 0.05, iy, z), new THREE.Vector3(maxX, iy, z)], 1.0, 1.0)
      add(box(0.22, 0.18, 0.14), panelDark, maxX + 0.9, iy + 0.3, z - 0.57)
      add(cyl(0.02, 0.2, 6), steel, maxX + 0.9, iy + 0.18, z - 0.57)
      stencil('OUTSIDE AIR →', maxX + 1.0, iy - 0.25, z - 0.506, 'z-', 0.9, '#f2f4f6')
    }
    // The rating plate: a physical nameplate, design data only.
    const plate = new THREE.Mesh(
      new THREE.PlaneGeometry(0.5, 0.31),
      new THREE.MeshBasicMaterial({ map: signTexture(['MAU-1', '12 000 m³/h', 'CHW 6/12 °C · HW 80/60 °C'], '#e4e7ea', '#22262c') }),
    )
    plate.position.set(centre['DISCHARGE'], yBase + 0.6, front - 0.012)
    plate.rotation.y = Math.PI
    g.add(plate)
    // The unit's own readout, live from its settings - and the click volume
    // that opens the bench where those settings are turned.
    {
      const x = centre['DISCHARGE']
      add(box(0.5, 0.32, 0.03), panelDark, x, yBase + 1.25, front - 0.015)
      const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.42, 0.16), new THREE.MeshBasicMaterial({ map: mauDisplay.texture }))
      screen.position.set(x, yBase + 1.25, front - 0.032)
      screen.rotation.y = Math.PI
      g.add(screen)
    }
    // Its beacon, like every skid's, driven from the same house state.
    const beaconMat = new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x1f8f4a, emissiveIntensity: 0.9, roughness: 0.4 })
    const beacon = new THREE.Mesh(cyl(0.07, 0.16, 10), beaconMat)
    beacon.position.set(centre['DISCHARGE'] + 0.5, yBase + h + 0.31, front + 0.2)
    g.add(beacon)
    add(cyl(0.02, 0.5, 6), steel, centre['DISCHARGE'] + 0.5, yBase + h + 0.0, front + 0.2)
    beacons.set('mau', beaconMat)
    const trigger = new THREE.Mesh(box(maxX - minX + 0.4, 2.6, depth + 0.4), new THREE.MeshBasicMaterial({ visible: false }))
    trigger.position.set((minX + maxX) / 2, floorY + 1.3, z)
    trigger.userData.skidSystem = 'mau'
    triggers.push(trigger)
    g.add(trigger)
  }

  // Return-air chases: the way up for the plenum's air, a louvred face each.
  for (const c of RETURN_CHASES) {
    const hC = ceilY - floorY
    add(box(c.w, hC, c.d), chase, c.x, floorY + hC / 2, c.z)
    for (const dy of [0.6, 1.6, 2.4]) add(box(c.w + 0.04, 0.04, c.d + 0.04), strut, c.x, floorY + dy, c.z)
    louvreTex.repeat.set(1, 8)
    const faceX = c.x + c.facing * (c.w / 2 + 0.006)
    const face = add(new THREE.PlaneGeometry(c.d - 0.4, 1.7), louvre, faceX, floorY + 1.15, c.z)
    face.rotation.y = c.facing > 0 ? Math.PI / 2 : -Math.PI / 2
    stencil('RETURN AIR ↑', faceX + c.facing * 0.002, floorY + 2.25, c.z, c.facing > 0 ? 'x+' : 'x-', 1.1)
    fireDamper(c.x, c.z, c.w, c.d, c.z > 0 ? -1 : 1)
  }

  // The pressurisation cascade, as the design placard by the utility board:
  // why the door at the top of the stair pushes back.
  {
    const cascade = new THREE.Mesh(
      new THREE.PlaneGeometry(0.9, 0.56),
      new THREE.MeshBasicMaterial({
        map: signTexture(
          ['PRESSURISATION CASCADE · DESIGN', 'CLEANROOM  +15 Pa', 'SUB-FAB  +5 Pa', 'AMBIENT  0 Pa'],
          '#e4e7ea',
          '#22262c',
          384,
          240,
        ),
      }),
    )
    cascade.position.set(UTILITY_BOARD.x - 2.2, floorY + 1.75, UTILITY_BOARD.z + UTILITY_BOARD.facing * 0.14)
    cascade.rotation.y = UTILITY_BOARD.facing > 0 ? 0 : Math.PI
    g.add(cascade)
  }

  // ------------------------------------------------------ fire protection
  //
  // The sprinkler grid: a riser with its control valve where a valve has to
  // be found in a hurry, a cross main at the west end, and branch lines the
  // length of the room with upright heads along them. See SPRINKLERS for why
  // it runs where it runs - under the beams, beside the racks, last in.
  //
  // It is drawn in the ordinary red of fire service pipe, which is the one
  // colour in this room that means "do not touch this to fix anything".
  {
    const fire = std(0xb8352b, 0.55, 0.25)
    const sy = SPRINKLERS.y
    const mainX = SPRINKLERS.mainX
    const zs = SPRINKLERS.branchZ
    // The riser: off the slab, up to the main, with the valve at eye height
    // and a gauge above it.
    pipeRun(
      fire,
      [
        new THREE.Vector3(SPRINKLERS.riser.x, floorY, SPRINKLERS.riser.z),
        new THREE.Vector3(SPRINKLERS.riser.x, sy, SPRINKLERS.riser.z),
        new THREE.Vector3(mainX, sy, SPRINKLERS.riser.z),
      ],
      0.075,
    )
    valve(fire, SPRINKLERS.riser.x, floorY + 1.5, SPRINKLERS.riser.z, 0.075, 'y')
    gauge(SPRINKLERS.riser.x + 0.16, floorY + 1.95, SPRINKLERS.riser.z, 1, 0.07)
    stencil('SPRINKLER RISER', SPRINKLERS.riser.x, floorY + 2.35, SPRINKLERS.riser.z + 0.19, 'z+', 0.8, '#f2f4f6')
    // The cross main, and a branch off it down each line.
    pipe(fire, new THREE.Vector3(mainX, sy, Math.min(...zs)), new THREE.Vector3(mainX, sy, Math.max(...zs)), 0.055)
    for (const z of zs) {
      pipe(fire, new THREE.Vector3(mainX, sy, z), new THREE.Vector3(SUBFAB.maxX - 0.6, sy, z), 0.035)
      // Upright heads: a stub, a body, and the deflector that makes the
      // spray a spray. Hung off the branch, never off the ceiling.
      for (let x = mainX + 1.2; x < SUBFAB.maxX - 0.8; x += SPRINKLERS.headSpacing) {
        add(cyl(0.014, 0.09, 6), fire, x, sy + 0.075, z)
        add(cyl(0.022, 0.05, 8), std(0xb08d3a, 0.4, 0.6), x, sy + 0.14, z)
        add(box(0.075, 0.008, 0.02), std(0xb08d3a, 0.4, 0.6), x, sy + 0.17, z)
      }
      // A hanger every few metres, to the rib soffit above.
      for (let x = mainX + 2; x < SUBFAB.maxX - 1; x += 3.2) {
        add(cyl(0.008, ceilY - sy, 6), rod, x, (ceilY + sy) / 2, z)
      }
    }
  }

  // ------------------------------------------------- process support gear
  //
  // What stands under a tool because that tool needs it: dry pumps with
  // forelines up to the riser bundles, the slurry and chemistry cabinets
  // with their double-contained pipe and leak sensors, the acid waste
  // neutralisation skid, the compressed-air receiver feeding the dryer, and
  // the valve manifold box the dry gas reaches the tools through.
  {
    const frameMat = std(0x3a3f46, 0.6, 0.3)
    const pumpMat = std(0x596069, 0.68, 0.3)
    const cabinet = painted(0xced2d6)
    const sleeve = new THREE.MeshStandardMaterial({ color: 0xe8f0f4, roughness: 0.3, transparent: true, opacity: 0.35 })
    const slurryMat = std(0x7a8ca0, 0.5, 0.2)
    const chemMat = std(0x6aa0c8, 0.5, 0.2)
    const drum = std(0x2f6db5, 0.5, 0.2)
    // Every tray's lamp shares one material, driven from the house's chemical
    // delivery system: a leak anywhere lights every tray red, and the dialog's
    // readings say which tray is wet.
    if (!beacons.has('chem')) beacons.set('chem', new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x1f8f4a, emissiveIntensity: 0.9, roughness: 0.4 }))
    const leakLed = beacons.get('chem')!
    const dropFor = (tag?: string) => TOOL_DROPS.find((d) => d.assetTag === tag)
    /** The leak sensor every wet cabinet sits over: a tray, a puck, a light. */
    const leakDetection = (x: number, z: number, w: number, d: number, out: 1 | -1) => {
      add(box(w + 0.3, 0.05, d + 0.3), galv, x, floorY + 0.025, z)
      add(cyl(0.05, 0.03, 10), walkway, x + w / 2, floorY + 0.065, z + out * (d / 2 + 0.05))
      const led = new THREE.Mesh(cyl(0.012, 0.02, 6), leakLed)
      led.position.set(x + w / 2, floorY + 0.09, z + out * (d / 2 + 0.05))
      g.add(led)
    }
    /** Double-contained pipe: the chemistry inside a clear outer sleeve. */
    const containedRun = (mat: THREE.Material, points: THREE.Vector3[]) => {
      pipeRun(mat, points, 0.03)
      for (let i = 0; i + 1 < points.length; i++) pipe(sleeve, points[i], points[i + 1], 0.052)
    }

    for (const gr of SUPPORT_GEAR) {
      const { x, z, w, d } = gr
      const out: 1 | -1 = gr.facing > 0 ? -1 : 1
      const front = z + out * (d / 2)
      const face = out > 0 ? 'z+' : 'z-'
      const drop = dropFor(gr.assetTag)
      if (gr.kind === 'dryPump') {
        // A frame with the pump on it, its controller, its foreline up to the
        // tool's vacuum riser and its own exhaust to the exhaust riser.
        add(box(w, 0.12, d), frameMat, x, floorY + 0.06, z)
        for (const [dx, dz] of [
          [-w / 2 + 0.06, -d / 2 + 0.06],
          [w / 2 - 0.06, -d / 2 + 0.06],
          [-w / 2 + 0.06, d / 2 - 0.06],
          [w / 2 - 0.06, d / 2 - 0.06],
        ]) {
          add(box(0.06, 0.55, 0.06), frameMat, x + dx, floorY + 0.4, z + dz)
        }
        add(box(w, 0.04, d), frameMat, x, floorY + 0.68, z)
        add(box(0.6, 0.42, 0.5), pumpMat, x - 0.1, floorY + 0.93, z)
        add(cyl(0.16, 0.34, 12), steel, x + 0.33, floorY + 0.93, z).rotation.z = Math.PI / 2
        add(box(0.22, 0.28, 0.1), panelGrey, x - 0.25, floorY + 0.45, front + out * 0.06)
        add(cyl(0.015, 0.02, 8), pilotGreen, x - 0.25, floorY + 0.53, front + out * 0.115).rotation.x = Math.PI / 2
        stencil(gr.label, x, floorY + 0.22, front + out * 0.03, face, 0.9, '#f2f4f6')
        // Every pump shares one beacon material, driven from the house's pump
        // system; the click volume opens the pumps' own dialog.
        if (!beacons.has('pumps')) beacons.set('pumps', new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x1f8f4a, emissiveIntensity: 0.9, roughness: 0.4 }))
        const pumpBeacon = new THREE.Mesh(cyl(0.05, 0.12, 10), beacons.get('pumps')!)
        pumpBeacon.position.set(x + w / 2 - 0.1, floorY + 1.3, front + out * 0.02)
        g.add(pumpBeacon)
        add(cyl(0.015, 0.3, 6), steel, x + w / 2 - 0.1, floorY + 1.1, front + out * 0.02)
        const pumpTrigger = new THREE.Mesh(box(w + 0.3, 1.8, d + 0.3), new THREE.MeshBasicMaterial({ visible: false }))
        pumpTrigger.position.set(x, floorY + 0.9, z)
        pumpTrigger.userData.skidSystem = 'pumps'
        triggers.push(pumpTrigger)
        g.add(pumpTrigger)
        if (drop) {
          // The foreline is the tool's own: from this pump straight up
          // through the slab into that chamber, on a riser of its own at
          // the end of the bundle. It never touches the house vacuum line
          // beside it - a process chamber shares its pump with nothing.
          const riserX = drop.x + RISER_DX.foreline
          pipeRun(
            steel,
            [
              new THREE.Vector3(x - 0.1, floorY + 1.14, z),
              new THREE.Vector3(x - 0.1, floorY + 1.3, z),
              new THREE.Vector3(riserX, floorY + 1.3, drop.z),
              new THREE.Vector3(riserX, ceilY + COFFER.depth, drop.z),
            ],
            0.045,
          )
          // The bellows where the foreline meets the pump.
          for (let i = 0; i < 4; i++) add(new THREE.TorusGeometry(0.06, 0.012, 6, 12), steel, x - 0.1, floorY + 1.17 + i * 0.03, z).rotation.x = Math.PI / 2
          // A dry pump runs on a nitrogen purge - it keeps process by-products
          // from condensing in the stages - and takes it from the bundle's
          // nitrogen down-leg by a small line with its own valve.
          {
            const n2 = lineOf('n2')
            const rack = PIPE_RACKS.reduce((a, b) => (Math.abs(b.z - drop.z) < Math.abs(a.z - drop.z) ? b : a))
            const side = Math.sign(rack.z) || 1
            const nx = drop.x + n2.riserDx
            const zDown = drop.z - side * 0.22
            pipeRun(
              lineMat.n2,
              [
                new THREE.Vector3(nx, floorY + 1.0, zDown),
                new THREE.Vector3(nx, floorY + 0.8, zDown),
                new THREE.Vector3(x - 0.1, floorY + 0.8, zDown),
                new THREE.Vector3(x - 0.1, floorY + 0.8, z),
              ],
              0.012,
            )
            valve(lineMat.n2, nx, floorY + 0.9, zDown, 0.014, 'y')
          }
          // The chamber's exhaust does NOT leave for the stack from here. It
          // goes to this tool's own abatement unit, standing on the riser
          // side, and that unit's outlet is what joins the house header - see
          // the note on SUPPORT_GEAR. The run used to go straight up, which
          // drew a room where raw process gas met the house scrubber.
          const abatement = SUPPORT_GEAR.find((o) => o.kind === 'abatement' && o.assetTag === gr.assetTag)
          if (abatement) {
            const into = abatement.z - Math.sign(abatement.z - z) * (abatement.d / 2)
            pipeRun(
              galv,
              [
                new THREE.Vector3(x + 0.33, floorY + 1.1, z),
                new THREE.Vector3(x + 0.33, floorY + 1.42, z),
                new THREE.Vector3(abatement.x, floorY + 1.42, z),
                new THREE.Vector3(abatement.x, floorY + 1.42, into),
              ],
              0.03,
            )
          }
        }
      } else if (gr.kind === 'slurry' || gr.kind === 'chemCabinet') {
        const mat = gr.kind === 'slurry' ? slurryMat : chemMat
        leakDetection(x, z, w, d, out)
        add(box(w, 1.9, d), cabinet, x, floorY + 1.0, z)
        add(box(w - 0.16, 1.6, 0.015), std(0xc5c9ce, 0.45, 0.25), x, floorY + 1.0, front + out * 0.008)
        add(box(0.03, 0.22, 0.05), black, x + w / 2 - 0.14, floorY + 1.0, front + out * 0.03)
        add(box(0.42, 0.28, 0.01), panelDark, x, floorY + 1.55, front + out * 0.017)
        stencil(gr.label, x, floorY + 1.85, front + out * 0.012, face, Math.min(w - 0.1, 1.1))
        // The cabinet's click volume opens the chemical delivery dialog.
        const chemTrigger = new THREE.Mesh(box(w + 0.3, 2.0, d + 0.3), new THREE.MeshBasicMaterial({ visible: false }))
        chemTrigger.position.set(x, floorY + 1.0, z)
        chemTrigger.userData.skidSystem = 'chem'
        triggers.push(chemTrigger)
        g.add(chemTrigger)
        if (gr.kind === 'slurry') {
          // The day tank's agitator on the roof, and the recirculation loop:
          // slurry settles, so it never stands still.
          add(cyl(0.12, 0.3, 12), black, x - 0.25, floorY + 2.1, z)
          stencil('KEEP AGITATED', x, floorY + 1.3, front + out * 0.012, face, 0.7, '#8a2f2a')
        } else {
          const corrosive = new THREE.Mesh(
            new THREE.PlaneGeometry(0.34, 0.34),
            new THREE.MeshBasicMaterial({ map: signTexture(['CORROSIVE', 'DOUBLE', 'CONTAINED'], '#f0c020', '#1e2024', 160, 160) }),
          )
          corrosive.position.set(x, floorY + 1.15, front + out * 0.013)
          corrosive.rotation.y = out > 0 ? 0 : Math.PI
          g.add(corrosive)
        }
        if (drop) {
          // Supply and return, double-contained, up into the tool's bundle
          // on a riser of their own.
          for (const [i, dx] of [-0.15, 0.15].entries()) {
            containedRun(mat, [
              new THREE.Vector3(x + dx, floorY + 1.95, z),
              new THREE.Vector3(x + dx, ceilY - 1.0 - i * 0.12, z),
              new THREE.Vector3(drop.x - 0.75 + dx, ceilY - 1.0 - i * 0.12, drop.z),
              new THREE.Vector3(drop.x - 0.75 + dx, ceilY + COFFER.depth, drop.z),
            ])
          }
        }
      } else if (gr.kind === 'awn') {
        // Two treatment tanks with their mixers, the dosing drums either
        // side, the pH controller, and the outlet to the industrial sewer.
        add(box(w, 0.15, d), steel, x, floorY + 0.075, z)
        for (const dx of [-0.95, 0.35]) {
          add(cyl(0.65, 1.7, 20), frp, x + dx, floorY + 1.0, z)
          add(new THREE.SphereGeometry(0.65, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2), frp, x + dx, floorY + 1.85, z)
          add(cyl(0.68, 0.06, 20), steel, x + dx, floorY + 1.0, z)
          add(cyl(0.1, 0.35, 12), black, x + dx, floorY + 2.5, z)
          add(cyl(0.03, 0.9, 6), steel, x + dx, floorY + 1.95, z)
          // Level sight tube on the front of each tank.
          add(cyl(0.02, 1.4, 8), sightGlass, x + dx + 0.5, floorY + 1.0, front + out * 0.05)
        }
        add(cyl(0.07, 0.4, 10), steel, x - 0.3, floorY + 1.9, z)
        pipe(frp, new THREE.Vector3(x - 0.95, floorY + 1.9, z), new THREE.Vector3(x + 0.35, floorY + 1.9, z), 0.05)
        // Caustic (blue) and acid (white) dosing drums with their pumps.
        for (const [dx, m] of [
          [1.35, drum],
          [1.75, white],
        ] as Array<[number, THREE.Material]>) {
          add(cyl(0.18, 0.85, 14), m, x + dx, floorY + 0.575, z + 0.3)
          add(box(0.14, 0.1, 0.14), black, x + dx, floorY + 1.05, z + 0.3)
          pipe(m === drum ? chemMat : white, new THREE.Vector3(x + dx, floorY + 1.1, z + 0.3), new THREE.Vector3(x + dx, floorY + 2.15, z + 0.3), 0.015)
          pipe(m === drum ? chemMat : white, new THREE.Vector3(x + dx, floorY + 2.15, z + 0.3), new THREE.Vector3(x + 0.35, floorY + 2.15, z + 0.3), 0.015)
        }
        // The pH controller, and what it stands for.
        add(box(0.4, 0.5, 0.12), panelGrey, x + 1.55, floorY + 1.55, front + out * 0.06)
        add(box(0.3, 0.14, 0.01), panelDark, x + 1.55, floorY + 1.66, front + out * 0.125)
        stencil('pH', x + 1.55, floorY + 1.45, front + out * 0.125, face, 0.2)
        for (const [i, m] of [pilotGreen, pilotAmber].entries()) {
          add(cyl(0.015, 0.02, 8), m, x + 1.45 + i * 0.1, floorY + 1.36, front + out * 0.125).rotation.x = Math.PI / 2
        }
        // In from the drains, out to the sewer through the wall.
        pipe(frp, new THREE.Vector3(x - 0.95, floorY + 0.02, front + out * 1.2), new THREE.Vector3(x - 0.95, floorY + 0.02, z), 0.05)
        pipe(frp, new THREE.Vector3(x - 0.95, floorY + 0.02, z), new THREE.Vector3(x - 0.95, floorY + 0.9, z), 0.05)
        pipe(frp, new THREE.Vector3(x + 0.35, floorY + 0.5, z), new THREE.Vector3(x + 0.35, floorY + 0.5, z - out * (d / 2 + 0.8)), 0.05)
        stencil('TO IWW →', x + 0.35, floorY + 0.75, z - out * (d / 2 + 0.6), face, 0.5, '#f2f4f6')
        stencil(gr.label, x, floorY + 0.35, front + out * 0.08, face, 1.6, '#f2f4f6')
      } else if (gr.kind === 'supportBank') {
        // Fab B only (subfabLayout.ts `ballroomSupportBanks`): the RF
        // generator, chiller and controller rack that Fab A's chase carries
        // behind each tool, standing under the tool here instead - a
        // cabinet with its louvres and screen on the walkway side, and a
        // cable trunk up through the slab to the mainframe it serves.
        add(box(w, 1.9, d), cabinet, x, floorY + 1.0, z)
        for (const dx of [-0.7, 0.05]) add(box(0.62, 0.7, 0.02), louvre, x + dx, floorY + 1.35, front + out * 0.012)
        add(box(0.34, 0.24, 0.02), std(0x1b2027, 0.35, 0.1), x + 0.85, floorY + 1.52, front + out * 0.012)
        add(cyl(0.015, 0.02, 8), pilotGreen, x + 0.85, floorY + 1.72, front + out * 0.02).rotation.x = Math.PI / 2
        stencil(gr.label, x - 0.3, floorY + 0.45, front + out * 0.03, face, 1.1, '#22262c')
        add(box(0.24, ceilY - (floorY + 1.95), 0.16), galv, x, (ceilY + floorY + 1.95) / 2, z)
      } else if (gr.kind === 'receiver') {
        // The vertical receiver the compressor fills, feeding the dryer.
        add(cyl(0.48, 2.0, 20), steel, x, floorY + 1.25, z)
        add(new THREE.SphereGeometry(0.48, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2), steel, x, floorY + 2.25, z)
        for (const a of [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3]) {
          add(box(0.08, 0.25, 0.08), frameMat, x + Math.cos(a) * 0.4, floorY + 0.125, z + Math.sin(a) * 0.4)
        }
        gauge(x, floorY + 1.6, front + out * 0.02, out, 0.07)
        add(cyl(0.03, 0.2, 8), steel, x + 0.2, floorY + 2.6, z)
        stencil(gr.label, x, floorY + 0.9, front + out * 0.03, face, 0.8, '#22262c')
        // Filled from the compressors, which are in a room of their own -
        // nobody puts a screw compressor's noise under a fab floor.
        pipe(pipeMat.gas, new THREE.Vector3(x, floorY + 1.0, SUBFAB.minZ + 0.1), new THREE.Vector3(x, floorY + 1.0, z - 0.3), 0.04)
        stencil('CDA FROM COMPRESSOR ROOM', x, floorY + 2.45, SUBFAB.minZ + 0.14, 'z+', 1.0)
        // Into the dryer's inlet filter, which is the point of it.
        const dryer = SKIDS.find((k) => k.kind === 'dryer')
        if (dryer) {
          pipeRun(
            pipeMat.gas,
            [
              new THREE.Vector3(x, floorY + 2.7, z),
              new THREE.Vector3(x, floorY + 2.8, z),
              new THREE.Vector3(dryer.pos[0] - 1.85, floorY + 2.8, z),
              new THREE.Vector3(dryer.pos[0] - 1.85, floorY + 2.8, dryer.pos[1]),
              new THREE.Vector3(dryer.pos[0] - 1.85, floorY + 1.75, dryer.pos[1]),
            ],
            0.04,
          )
        }
      } else if (gr.kind === 'vacReceiver') {
        // A horizontal vessel on saddles under the end of the pump header,
        // teed into it from above: the header's reservoir. Black, like the
        // header - it is the same system.
        const len = d - 0.9
        const cy = floorY + 0.75
        add(cyl(0.45, len, 20), lineMat.vacuum, x, cy, z).rotation.x = Math.PI / 2
        for (const dz of [-len / 2, len / 2]) add(new THREE.SphereGeometry(0.45, 20, 10), lineMat.vacuum, x, cy, z + dz)
        for (const dz of [-0.55, 0.55]) add(box(w - 0.1, 0.3, 0.25), frameMatDark, x, floorY + 0.15, z + dz)
        // Up into the header, with the isolation valve on the tee.
        pipe(lineMat.vacuum, new THREE.Vector3(x, cy + 0.4, z), new THREE.Vector3(x, floorY + 2.3, z), 0.08)
        valve(lineMat.vacuum, x, floorY + 1.7, z, 0.08, 'y')
        // The vacuum gauge on top, and a drain at the bottom of the shell.
        gauge(x + 0.35, cy + 0.62, z, out, 0.07)
        add(cyl(0.02, 0.2, 6), steel, x, floorY + 0.2, z + 0.3)
        add(box(0.03, 0.02, 0.03), red, x, floorY + 0.12, z + 0.3)
        stencil(gr.label, x, cy, front + out * 0.02, face, 0.9, '#f2f4f6')
      } else if (gr.kind === 'vmb') {
        // The valve manifold box: the dry-gas header comes down into it, and
        // the tool lines leave it one valve each.
        add(box(w, 1.2, d), galv, x, floorY + 1.45, z)
        add(box(w - 0.1, 1.05, 0.012), std(0xbcc0c5, 0.45, 0.25), x, floorY + 1.45, front + out * 0.007)
        add(box(0.025, 0.16, 0.04), black, x + w / 2 - 0.1, floorY + 1.45, front + out * 0.03)
        // Fed from the rack, one line each: nitrogen down into one side of
        // the box, air into the other, an isolation valve on each stub.
        {
          const rack = PIPE_RACKS[0]
          for (const [dx, sv] of [
            [-0.25, lineOf('n2')],
            [0.25, lineOf('cda')],
          ] as Array<[number, Service]>) {
            const vx = x + dx
            pipeRun(
              lineMat[sv.line],
              [
                new THREE.Vector3(vx, floorY + 2.05, z),
                new THREE.Vector3(vx, HOP_Y, z),
                new THREE.Vector3(vx, HOP_Y, rack.z + sv.dz),
                new THREE.Vector3(vx, lineY(sv), rack.z + sv.dz),
              ],
              sv.r * 0.7,
            )
            valve(lineMat[sv.line], vx, floorY + 2.22, z, 0.025, 'y')
          }
        }
        for (const [i, m] of [pilotGreen, pilotGreen, pilotAmber].entries()) {
          add(cyl(0.012, 0.02, 8), m, x - 0.25 + i * 0.1, floorY + 1.9, front + out * 0.02).rotation.x = Math.PI / 2
        }
        stencil(gr.label, x, floorY + 1.0, front + out * 0.015, face, 0.8)
      } else if (gr.kind === 'abatement') {
        // A burn-wet point-of-use unit: a combustor on top of a wet section.
        // The pump's line comes into the side at 1.42 m, the products are
        // burned with the fuel and air a student can see teed in, the gases
        // are quenched and washed on the way down, and only then does the
        // outlet duct climb to the exhaust riser. What leaves the bottom is
        // acid water, so there is a drain to the neutralisation skid - that
        // drain is the reason the AWN skid at the other end of the room has
        // anything to neutralise.
        add(box(w, 0.1, d), frameMatDark, x, floorY + 0.05, z)
        add(box(w, 1.85, d), cabinet, x, floorY + 1.02, z)
        add(box(w - 0.14, 1.45, 0.015), std(0xc5c9ce, 0.45, 0.25), x, floorY + 1.0, front + out * 0.008)
        add(box(0.03, 0.2, 0.05), black, x + w / 2 - 0.13, floorY + 1.0, front + out * 0.03)
        // The combustor's sight glass, the one window in the room that has a
        // flame behind it, and the panel under it.
        add(cyl(0.05, 0.02, 12), std(0xff8c3a, 0.3, 0), x - 0.2, floorY + 1.72, front + out * 0.012).rotation.x = Math.PI / 2
        add(box(0.34, 0.24, 0.02), panelDark, x + 0.15, floorY + 1.68, front + out * 0.014)
        add(cyl(0.012, 0.02, 8), pilotGreen, x - 0.02, floorY + 1.68, front + out * 0.025).rotation.x = Math.PI / 2
        // Inlet spigot on the pump side, outlet stack on top.
        const fromPump = Math.sign((SUPPORT_GEAR.find((o) => o.kind === 'dryPump' && o.assetTag === gr.assetTag)?.z ?? z) - z) || -1
        add(cyl(0.05, 0.16, 10), galv, x, floorY + 1.42, z + fromPump * (d / 2 + 0.05)).rotation.x = Math.PI / 2
        // Fuel and air to the combustor, off the bundle's own dry-gas legs.
        for (const [dx, sv] of [
          [-0.16, lineOf('n2')],
          [0.16, lineOf('cda')],
        ] as Array<[number, Service]>) {
          pipeRun(
            lineMat[sv.line],
            [
              new THREE.Vector3(x + dx, floorY + 1.95, z - fromPump * (d / 2 - 0.1)),
              new THREE.Vector3(x + dx, floorY + 1.95, z - fromPump * (d / 2 + 0.18)),
              new THREE.Vector3(x + dx, floorY + 2.35, z - fromPump * (d / 2 + 0.18)),
            ],
            0.014,
          )
        }
        // The acid drain: out of the wet section, into a tundish on the slab
        // where a technician can see it running.
        pipeRun(galv, [
          new THREE.Vector3(x + 0.25, floorY + 0.3, front + out * (d / 2 - 0.06)),
          new THREE.Vector3(x + 0.25, floorY + 0.16, front + out * (d / 2 - 0.06)),
        ], 0.022)
        add(cyl(0.075, 0.09, 12), galv, x + 0.25, floorY + 0.11, front + out * (d / 2 - 0.06))
        stencil(gr.label, x, floorY + 0.4, front + out * 0.012, face, Math.min(w - 0.06, 0.85), '#f2f4f6')
        // On the house exhaust, so it lights and opens with that system.
        if (!beacons.has('hvac')) beacons.set('hvac', new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x1f8f4a, emissiveIntensity: 0.9, roughness: 0.4 }))
        const abateBeacon = new THREE.Mesh(cyl(0.045, 0.1, 10), beacons.get('hvac')!)
        abateBeacon.position.set(x + w / 2 - 0.12, floorY + 2.05, z)
        g.add(abateBeacon)
        const abateTrigger = new THREE.Mesh(box(w + 0.3, 2.1, d + 0.3), new THREE.MeshBasicMaterial({ visible: false }))
        abateTrigger.position.set(x, floorY + 1.05, z)
        abateTrigger.userData.skidSystem = 'hvac'
        triggers.push(abateTrigger)
        g.add(abateTrigger)
        if (drop) {
          // Only now to the stack: the treated outlet, up the exhaust riser
          // this tool shares with nothing.
          const exhX = drop.x + RISER_DX.hvac
          pipeRun(
            galv,
            [
              new THREE.Vector3(x, floorY + 1.95, z),
              new THREE.Vector3(x, ceilY - 1.15, z),
              new THREE.Vector3(x, ceilY - 1.15, drop.z),
              new THREE.Vector3(exhX, ceilY - 1.15, drop.z),
              new THREE.Vector3(exhX, ceilY - RACK.upperDrop + 0.1, drop.z),
            ],
            0.03,
          )
        }
      } else if (gr.kind === 'eyewash') {
        // The post, the bowl with its two heads and its paddle, the drench
        // shower over it with its pull rod, and the sign that is the whole
        // point: this has to be findable by someone who cannot see.
        add(cyl(0.035, 2.15, 10), walkway, x, floorY + 1.07, z)
        add(cyl(0.16, 0.2, 12), walkway, x, floorY + 0.02, z)
        add(cyl(0.19, 0.07, 14), std(0xd8dde1, 0.4, 0.35), x, floorY + 0.98, z)
        for (const dx of [-0.07, 0.07]) add(cyl(0.022, 0.06, 8), std(0x2f8a55, 0.5, 0.2), x + dx, floorY + 1.03, z)
        add(box(0.22, 0.03, 0.05), walkway, x + 0.2, floorY + 0.98, z)
        add(cyl(0.02, 0.42, 8), galv, x, floorY + 2.14, z + out * 0.21).rotation.x = Math.PI / 2
        add(cyl(0.17, 0.05, 16), std(0xd8dde1, 0.4, 0.35), x, floorY + 2.1, z + out * 0.42)
        add(cyl(0.012, 0.75, 6), steel, x + 0.16, floorY + 1.75, z + out * 0.42)
        add(new THREE.TorusGeometry(0.06, 0.012, 6, 14), walkway, x + 0.16, floorY + 1.4, z + out * 0.42)
        const sign = new THREE.Mesh(
          new THREE.PlaneGeometry(0.42, 0.3),
          new THREE.MeshBasicMaterial({ map: signTexture(['EMERGENCY', 'EYEWASH · SHOWER'], '#1f7a3c', '#f4f6f8', 256, 180) }),
        )
        sign.position.set(x, floorY + 1.85, z + out * 0.05)
        sign.rotation.y = out > 0 ? 0 : Math.PI
        g.add(sign)
        // The keep-clear it has to have: an obstructed shower is no shower.
        const pad = add(new THREE.PlaneGeometry(1.1, 1.1), walkway, x, floorY + 0.004, z)
        pad.rotation.x = -Math.PI / 2
      } else if (gr.kind === 'drums') {
        // A bunded pallet - the bund is sized to hold the biggest drum on it
        // if that drum lets go - four drums, and the spill kit beside them.
        add(box(w, 0.16, d), galv, x, floorY + 0.08, z)
        for (const [dx, dz] of [
          [-w / 2 + 0.03, 0],
          [w / 2 - 0.03, 0],
          [0, -d / 2 + 0.03],
          [0, d / 2 - 0.03],
        ]) {
          add(box(dz === 0 ? 0.06 : w, 0.22, dz === 0 ? d : 0.06), galv, x + dx, floorY + 0.19, z + dz)
        }
        for (const [dx, dz] of [
          [-0.42, -0.22],
          [-0.42, 0.22],
          [0.08, -0.22],
          [0.08, 0.22],
        ]) {
          add(cyl(0.28, 0.86, 16), drum, x + dx, floorY + 0.62, z + dz)
          for (const ry of [0.35, 0.62, 0.89]) add(new THREE.TorusGeometry(0.281, 0.012, 6, 18), drum, x + dx, floorY + ry, z + dz).rotation.x = Math.PI / 2
        }
        add(box(0.5, 0.46, 0.42), walkway, x + w / 2 - 0.3, floorY + 0.4, z)
        add(box(0.46, 0.03, 0.38), black, x + w / 2 - 0.3, floorY + 0.63, z)
        // On the bund itself, where a store's label goes - not floating over
        // the drums, which is where it sat when it was first drawn.
        stencil(gr.label, x - 0.15, floorY + 0.22, z + out * (d / 2 + 0.035), face, 1.0, '#f2f4f6')
      }
    }

    // Where the nitrogen comes from: the bulk tank and vaporiser outside,
    // through the north wall beside the manifold box, and onto the rack.
    {
      const n2 = lineOf('n2')
      const rack = PIPE_RACKS[0]
      const ex = 27.9
      const sx = 28.4
      pipeRun(
        lineMat.n2,
        [
          new THREE.Vector3(ex, HOP_Y, SUBFAB.minZ + 0.1),
          new THREE.Vector3(ex, HOP_Y, rack.z + n2.dz),
          new THREE.Vector3(ex, lineY(n2), rack.z + n2.dz),
        ],
        n2.r,
      )
      valve(lineMat.n2, ex, HOP_Y, SUBFAB.minZ + 0.6, n2.r, 'z')
      stencil('N2 FROM BULK TANK', sx, HOP_Y - 0.3, SUBFAB.minZ + 0.14, 'z+', 0.8)
    }

    // Oxygen-deficiency monitors: a display, a lamp and a label, at head
    // height on the walls where the nitrogen pipe runs.
    for (const m of O2_MONITORS) {
      const holder = new THREE.Group()
      holder.position.set(m.x, floorY + 1.7, m.z)
      holder.rotation.y = m.rot
      const body = new THREE.Mesh(box(0.2, 0.28, 0.08), panelGrey)
      body.position.z = 0.04
      holder.add(body)
      const screen = new THREE.Mesh(
        new THREE.PlaneGeometry(0.15, 0.08),
        new THREE.MeshBasicMaterial({ map: signTexture(['O₂ 20.9 %'], '#0f1c14', '#7ff0a0', 192, 96) }),
      )
      screen.position.set(0, 0.06, 0.081)
      holder.add(screen)
      const lamp = new THREE.Mesh(cyl(0.015, 0.02, 8), pilotGreen)
      lamp.rotation.x = Math.PI / 2
      lamp.position.set(0, -0.06, 0.085)
      holder.add(lamp)
      const label = new THREE.Mesh(
        new THREE.PlaneGeometry(0.26, 0.09),
        new THREE.MeshBasicMaterial({ map: signTexture(['O₂ DEFICIENCY MONITOR'], '#1e4fa3', '#ffffff', 256, 88) }),
      )
      label.position.set(0, 0.22, 0.005)
      holder.add(label)
      g.add(holder)
    }
  }

  // ------------------------------------------------------- safety furniture
  //
  // What says people work here: extinguishers on the columns nearest the
  // walkway, the eyewash by the chemistry, the signs a facilities crew
  // actually hangs.
  for (const [x, z] of COLUMNS.filter(([, cz2]) => Math.abs(cz2) < 4).slice(0, 4)) {
    const out = z > 0 ? -1 : 1
    add(cyl(0.085, 0.5, 12), red, x, floorY + 1.05, z + out * 0.4)
    add(cyl(0.03, 0.08, 8), black, x, floorY + 1.34, z + out * 0.4)
    add(box(0.12, 0.08, 0.1), black, x, floorY + 1.05, z + out * 0.32)
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(0.2, 0.25),
      new THREE.MeshBasicMaterial({ map: signTexture(['FIRE', 'EXT.'], '#c8362a', '#ffffff', 128, 160) }),
    )
    sign.position.set(x, floorY + 1.6, z + out * 0.31)
    sign.rotation.y = out > 0 ? 0 : Math.PI
    g.add(sign)
  }
  {
    // Eyewash and drench showers within a few steps of every place a
    // corrosive is handled: by the UPW plant, and by the neutralisation
    // skid with its acid and caustic dosing drums. A station is a bowl at
    // waist height, a shower head overhead, and the green sign that finds
    // it for a person who cannot see.
    const bowlMat = std(0x3f8f4a, 0.5)
    const headMat = std(0xd8e232, 0.5)
    const eyewashSign = signTexture(['EMERGENCY', 'EYEWASH', '& SHOWER'], '#1f7a3c', '#f2fff5', 128, 160)
    /** (wx, wz) is a point 0.4 m off the wall; (nx, nz) the wall's normal into the room. */
    const eyewash = (wx: number, wz: number, nx: number, nz: number, signRot: number): void => {
      const P = (o: number, s: number): [number, number] => [wx + nx * o + nz * s, wz + nz * o - nx * s]
      let p = P(0, 0)
      add(box(nz ? 0.35 : 0.25, 0.5, nz ? 0.25 : 0.35), bowlMat, p[0], floorY + 1.0, p[1])
      p = P(0.25, 0)
      add(cyl(0.18, 0.06, 14), headMat, p[0], floorY + 1.05, p[1])
      add(cyl(0.03, 1.05, 8), galv, p[0], floorY + 0.5, p[1])
      p = P(0.15, 0.35)
      add(cyl(0.025, 2.4, 8), galv, p[0], floorY + 1.2, p[1])
      p = P(0.4, 0.35)
      add(cyl(0.16, 0.05, 14), headMat, p[0], floorY + 2.4, p[1])
      const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.5), new THREE.MeshBasicMaterial({ map: eyewashSign }))
      p = P(0.14, 0)
      sign.position.set(p[0], floorY + 1.95, p[1])
      sign.rotation.y = signRot
      g.add(sign)
    }
    eyewash(SUBFAB.maxX - 0.4, 6.3, -1, 0, -Math.PI / 2)
    eyewash(38.5, SUBFAB.minZ + 0.4, 0, 1, 0)
  }
  {
    // A room this size needs a second way out, and it cannot be the
    // airlock: a fire exit in the east wall, out to the exterior stair,
    // with its panic bar, its sign and its keep-clear.
    const x = SUBFAB.maxX - 0.13
    const z = -1.5
    add(box(0.08, 2.14, 1.06), std(0x5a6068, 0.6, 0.3), x, floorY + 1.07, z)
    add(box(0.05, 2.04, 0.96), std(0x8d939a, 0.55, 0.3), x - 0.04, floorY + 1.02, z)
    add(box(0.04, 0.06, 0.8), steel, x - 0.09, floorY + 1.0, z)
    const exit = new THREE.Mesh(new THREE.PlaneGeometry(0.42, 0.21), exitMat)
    exit.position.set(x - 0.08, floorY + 2.4, z)
    exit.rotation.y = -Math.PI / 2
    g.add(exit)
    const kc = add(new THREE.PlaneGeometry(1.2, 1.4), hazard, x - 0.75, floorY + 0.005, z)
    kc.rotation.x = -Math.PI / 2
  }
  for (const [x, z, rot, lines, bg, fg] of [
    [SUBFAB.minX + 0.14, 0, Math.PI / 2, ['HEARING', 'PROTECTION', 'REQUIRED'], '#1e4fa3', '#ffffff'],
    [SUBFAB.maxX - 0.14, -0.45, -Math.PI / 2, ['FIRE EXIT', 'KEEP CLEAR'], '#f2f4f6', '#b3261e'],
    [30, SUBFAB.maxZ - 0.14, Math.PI, ['CAUTION', 'OVERHEAD', 'PIPING'], '#e8b83a', '#1e2024'],
  ] as Array<[number, number, number, string[], string, string]>) {
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 0.375), new THREE.MeshBasicMaterial({ map: signTexture(lines, bg, fg) }))
    sign.position.set(x, floorY + 1.9, z)
    sign.rotation.y = rot
    g.add(sign)
  }

  // ------------------------------------------------------------ utility board
  const boardCanvas = document.createElement('canvas')
  boardCanvas.width = 512
  boardCanvas.height = 448
  const boardTexture = new THREE.CanvasTexture(boardCanvas)
  boardTexture.colorSpace = THREE.SRGBColorSpace
  const board = new THREE.Mesh(new THREE.PlaneGeometry(2.1, 1.84), new THREE.MeshBasicMaterial({ map: boardTexture }))
  board.position.set(UTILITY_BOARD.x, floorY + 1.75, UTILITY_BOARD.z + UTILITY_BOARD.facing * 0.14)
  // facing says which side of its wall the board hangs on; the plane's normal
  // must point the same way, or the student reads the back of a one-sided mesh.
  board.rotation.y = UTILITY_BOARD.facing > 0 ? 0 : Math.PI
  g.add(board)
  // Its enclosure and the phone beside it - the one facilities picks up.
  add(box(2.2, 1.94, 0.1), panelDark, UTILITY_BOARD.x, floorY + 1.75, UTILITY_BOARD.z + UTILITY_BOARD.facing * 0.08)
  {
    const px = UTILITY_BOARD.x + 1.4
    const pz = UTILITY_BOARD.z + UTILITY_BOARD.facing * 0.08
    const f = UTILITY_BOARD.facing
    add(box(0.22, 0.3, 0.1), std(0xc8362a, 0.5), px, floorY + 1.5, pz)
    // The handset in its cradle on the front, and the cord.
    add(cyl(0.028, 0.22, 8), black, px - 0.06, floorY + 1.5, pz + f * 0.08)
    add(cyl(0.008, 0.35, 6), black, px + 0.06, floorY + 1.28, pz + f * 0.06)
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(0.3, 0.19),
      new THREE.MeshBasicMaterial({ map: signTexture(['FACILITIES', 'DIAL 4100'], '#f2f4f6', '#22262c', 256, 160) }),
    )
    sign.position.set(px, floorY + 1.82, pz + f * 0.06)
    sign.rotation.y = f > 0 ? 0 : Math.PI
    g.add(sign)
  }

  // ---------------------------------------------------------------- lighting
  // Utilitarian strip fixtures: an emissive tube in a batten per lamp so the
  // light has a visible source. The PointLights join the fab's shared pool,
  // so the per-tier visible-light budget - the constant that killed the
  // walking hitch - still holds; nearest-K in 3D picks these up only when the
  // student is actually down here.
  // The lights themselves came in as 'lamps' (buildSubfabLamps); this is
  // their battens, at the same positions, so the circuit can be run to them.
  const stripMat = new THREE.MeshBasicMaterial({ color: 0xeef2ec })
  const fixtures = subfabLampPositions()
  for (const [x, z] of fixtures) {
    add(box(1.4, 0.07, 0.2), panelGrey, x, ceilY - 0.05, z)
    add(box(1.3, 0.05, 0.13), stripMat, x, ceilY - 0.095, z)
  }

  // The lighting circuit.
  //
  // Every other load in this room now has a conduit to it and a switch that
  // kills it; the strips were the last thing hanging off nothing. A batten
  // is not wireless, and this is the run a sparky would put in: tight under
  // the soffit, alongside the row rather than through it, a junction box and
  // a short whip at each fixture, and both rows picked up from the cable
  // tray on the nearest rack - the same tray the MCC's wall run feeds, so
  // the light over a student's head comes from the lineup they were just
  // standing in front of.
  //
  // The stair lamp is on a spur off the south row, because it is one fitting
  // and one fitting does not earn a circuit of its own.
  {
    const runY = ceilY - 0.035
    const rows = new Map<number, number[]>()
    for (const [x, z] of fixtures) rows.set(z, [...(rows.get(z) ?? []), x])
    const main = [...rows.entries()].filter(([, xs]) => xs.length > 1)
    for (const [z, xs] of main) {
      const runZ = z + 0.16
      const from = Math.min(...xs) - 0.5
      const to = Math.max(...xs) + 0.5
      pipe(conduitMat, new THREE.Vector3(from, runY, runZ), new THREE.Vector3(to, runY, runZ), 0.018)
      for (const x of xs) {
        // The junction box over the fitting, and the whip down into it.
        add(box(0.1, 0.08, 0.1), panelGrey, x, runY, runZ)
        pipeRun(
          conduitMat,
          [new THREE.Vector3(x, runY, runZ - 0.05), new THREE.Vector3(x, runY, z + 0.06), new THREE.Vector3(x, ceilY - 0.06, z + 0.06)],
          0.012,
        )
      }
      // Down to the tray that feeds the row.
      const rack = PIPE_RACKS.reduce((a, b) => (Math.abs(b.z - z) < Math.abs(a.z - z) ? b : a))
      const trayZ = rack.z - (Math.sign(rack.z) || 1) * 0.45
      const tapX = xs[Math.floor(xs.length / 2)] + 0.9
      pipeRun(
        conduitMat,
        [
          new THREE.Vector3(tapX, runY, runZ),
          new THREE.Vector3(tapX, runY, trayZ),
          new THREE.Vector3(tapX, upperY + 0.1, trayZ),
        ],
        0.018,
      )
      for (let hz = Math.min(runZ, trayZ) + 0.7; hz < Math.max(runZ, trayZ) - 0.4; hz += 1.2) {
        add(cyl(0.006, ceilY - runY, 6), rod, tapX, (ceilY + runY) / 2, hz)
      }
    }
    // The spur to the stair fitting, off whichever row is nearer.
    for (const [z, xs] of rows) {
      if (xs.length > 1) continue
      const x = xs[0]
      const row = main.reduce((a, b) => (Math.abs(b[0] - z) < Math.abs(a[0] - z) ? b : a))
      pipeRun(
        conduitMat,
        [
          new THREE.Vector3(x, runY, row[0] + 0.16),
          new THREE.Vector3(x, runY, z + 0.16),
          new THREE.Vector3(x, runY, z + 0.06),
          new THREE.Vector3(x, ceilY - 0.06, z + 0.06),
        ],
        0.012,
      )
    }
  }

  // ------------------------------------------------------------- merging
  //
  // Everything static, by material, into one mesh each. The room is built
  // from a few hundred small parts and would otherwise be a few hundred
  // draw calls, which the integrated GPU pays for twice on the high tier.
  {
    const byMaterial = new Map<THREE.Material, { geometries: THREE.BufferGeometry[]; meshes: THREE.Mesh[] }>()
    for (const mesh of statics) {
      mesh.updateMatrix()
      const geometry = mesh.geometry.clone().applyMatrix4(mesh.matrix)
      const bucket = byMaterial.get(mesh.material as THREE.Material) ?? { geometries: [], meshes: [] }
      bucket.geometries.push(geometry)
      bucket.meshes.push(mesh)
      byMaterial.set(mesh.material as THREE.Material, bucket)
    }
    for (const [material, { geometries, meshes }] of byMaterial) {
      const merged = geometries.length > 1 ? mergeGeometries(geometries, false) : null
      if (merged) {
        g.add(new THREE.Mesh(merged, material))
      } else {
        for (const m of meshes) g.add(m)
      }
    }
  }

  // ------------------------------------------------------------ live repaint
  let drawnKey = ''
  const update = (house: HouseState, armedId: string | null): void => {
    const armedSystem = armedId ? houseFault(armedId).system : null
    for (const [system, mat] of beacons) {
      const state = houseSystemState(house, system)
      const bad = state === 'critical' || system === armedSystem
      mat.emissive.set(bad ? 0xd23a2e : state === 'warning' ? 0xd8a020 : 0x1f8f4a)
      mat.color.set(bad ? 0x4a1512 : state === 'warning' ? 0x4a3a10 : 0x184a2a)
    }
    for (const [system, d] of displays) {
      const state = houseSystemState(house, system)
      const bad = state === 'critical' || system === armedSystem
      const text = systemValue(house, system)
      const key = text + (bad ? '!' : state)
      if (key === d.last) continue
      d.last = key
      const ctx = d.canvas.getContext('2d')!
      ctx.fillStyle = '#0f1c14'
      ctx.fillRect(0, 0, 256, 96)
      ctx.fillStyle = '#5a7a60'
      ctx.font = '600 16px ui-monospace, monospace'
      ctx.fillText(DISPLAY_LABEL[system], 12, 26)
      ctx.fillStyle = bad ? '#ff6b5e' : state === 'warning' ? '#f0c040' : '#7ff0a0'
      ctx.font = '700 36px ui-monospace, monospace'
      ctx.fillText(text, 12, 70)
      d.texture.needsUpdate = true
    }
    drawBoard(boardCanvas, boardTexture, house, armedSystem)
    // The make-up air unit's readout follows its own settings, not the house.
    {
      const s = mauStore.settings
      const r = mauStore.report
      const line1 = `${s.supplyC.toFixed(1)} °C  ${s.supplyRh.toFixed(0)} %RH`
      const line2 = `ROOM ${r.cleanroomPa >= 0 ? '+' : ''}${r.cleanroomPa.toFixed(0)} Pa`
      const bad = r.warnings.length > 0
      const key = line1 + line2 + bad
      if (key !== mauDisplay.last) {
        mauDisplay.last = key
        const ctx = mauDisplay.canvas.getContext('2d')!
        ctx.fillStyle = '#0f1c14'
        ctx.fillRect(0, 0, 256, 96)
        ctx.fillStyle = '#5a7a60'
        ctx.font = '600 14px ui-monospace, monospace'
        ctx.fillText('MAU-1 SUPPLY', 12, 22)
        ctx.fillStyle = '#7ff0a0'
        ctx.font = '700 26px ui-monospace, monospace'
        ctx.fillText(line1, 12, 52)
        ctx.fillStyle = bad ? '#f0c040' : '#7ff0a0'
        ctx.font = '700 22px ui-monospace, monospace'
        ctx.fillText(line2, 12, 82)
        mauDisplay.texture.needsUpdate = true
      }
    }
  }

  /** Redraw only when what would be drawn has changed - text is expensive. */
  function drawBoard(
    canvas: HTMLCanvasElement,
    texture: THREE.CanvasTexture,
    house: HouseState,
    armedSystem: HouseSystem | null,
  ): void {
    const systems = Object.keys(HOUSE_SYSTEMS) as HouseSystem[]
    const rows = systems.map((id) => {
      const label = t(`house.system.${id}.Label`)
      const value = systemValue(house, id)
      const state = houseSystemState(house, id)
      return { id, label, value, state }
    })
    const key = JSON.stringify(rows) + armedSystem
    if (key === drawnKey) return
    drawnKey = key

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#20242a'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#e8ecef'
    ctx.font = '600 30px system-ui, sans-serif'
    ctx.fillText(tOr('subfab.BoardTitle', 'SUB-FAB UTILITIES'), 24, 48)
    ctx.strokeStyle = '#3a4048'
    ctx.beginPath()
    ctx.moveTo(24, 64)
    ctx.lineTo(488, 64)
    ctx.stroke()

    rows.forEach((row, i) => {
      const y = 98 + i * 44
      ctx.fillStyle =
        row.state === 'critical' || row.id === armedSystem
          ? '#e0564a'
          : row.state === 'warning'
            ? '#d8a020'
            : '#4fae6d'
      ctx.beginPath()
      ctx.arc(40, y - 8, 9, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#e8ecef'
      ctx.font = '500 21px system-ui, sans-serif'
      ctx.fillText(row.label, 64, y)
      ctx.fillStyle = '#9aa2ab'
      ctx.font = '19px ui-monospace, monospace'
      ctx.textAlign = 'right'
      ctx.fillText(row.value, 488, y)
      ctx.textAlign = 'left'
    })
    texture.needsUpdate = true
  }

  const animate = (dt: number, eye: THREE.Vector3): void => {
    for (const fan of fans) {
      const rate = fan.userData.spinRate as number
      if (fan.userData.spinAxis === 'x') fan.rotation.x += rate * dt
      else if (fan.userData.spinAxis === 'z') fan.rotation.z += rate * dt
      else fan.rotation.y += rate * dt
    }
    // The airlock. A door wants to open for a person close to it on its own
    // level, and may only do so while the other door is shut - which is the
    // whole of an interlock. The collider follows the leaf past half-open.
    const wants = (d: Door): boolean => {
      const dx = eye.x - d.centre.x
      const dz = eye.z - d.centre.z
      const onLevel = d.level === 'fab' ? eye.y > 0.3 : eye.y < 0.5
      return onLevel && dx * dx + dz * dz < AIRLOCK.approach * AIRLOCK.approach
    }
    for (const which of ['upper', 'lower'] as const) {
      const d = doors[which]
      const other = doors[which === 'upper' ? 'lower' : 'upper']
      const target = wants(d) && other.open < 0.02 ? 1 : 0
      const before = d.open
      d.open += Math.sign(target - d.open) * Math.min(Math.abs(target - d.open), 1.4 * dt)
      if (d.axis === 'z') d.leaf.position.z = d.closed - d.open * d.travel
      else d.leaf.position.x = d.closed - d.open * d.travel
      if ((before > 0.5) !== (d.open > 0.5)) setAirlockDoor(which, d.open > 0.5)
      const interlocked = other.open >= 0.02
      d.free.emissiveIntensity = interlocked ? 0.15 : 1.0
      d.locked.emissiveIntensity = interlocked ? 1.0 : 0.15
    }
  }

  return { group: g, lamps, triggers, update, animate }
}

/** What a skid's own readout is labelled - a physical engraving, not UI copy. */
const DISPLAY_LABEL: Record<HouseSystem, string> = {
  vacuum: 'HEADER LEAK-UP',
  pcw: 'SUPPLY vs SETPOINT',
  gas: 'HEADER MOISTURE',
  upw: 'LOOP RESISTIVITY',
  hvac: 'DELIVERED FLOW',
  mau: 'MAKE-UP AIR',
  pumps: 'DRY PUMPS',
  chem: 'CHEM DELIVERY',
}

/** The one number a walking technician wants from each system. */
function systemValue(house: HouseState, system: HouseSystem): string {
  switch (system) {
    case 'vacuum':
      return house.vacuumHeaderLeak > 1
        ? `${house.vacuumHeaderLeak.toFixed(0)} mTorr/min`
        : tOr('subfab.Tight', 'tight')
    case 'pcw':
      return `${house.pcwOffsetC >= 0 ? '+' : ''}${house.pcwOffsetC.toFixed(0)} °C`
    case 'gas':
      return `${house.gasHeaderPpm.toFixed(0)} ppm`
    case 'upw':
      return `${house.upwMohmCm.toFixed(1)} MΩ·cm`
    case 'hvac':
      return `${(house.ahuDelivery * 100).toFixed(0)}%`
    case 'mau':
      return `${(house.mauDelivery * 100).toFixed(0)}% · ${house.cleanroomPa >= 0 ? '+' : ''}${house.cleanroomPa.toFixed(0)} Pa`
    case 'pumps':
      return house.dryPumpFailedTool ? `${house.dryPumpFailedTool} DOWN` : tOr('subfab.PumpsPulling', 'all pulling')
    case 'chem':
      return house.chemLeakTool ? `${house.chemLeakTool} LEAK` : tOr('subfab.TraysDry', 'trays dry')
  }
}
